import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, Music, Play, Volume2, VolumeX, Eye, Download, Loader2, Zap, Crown, ChevronLeft, ChevronRight, Bookmark, EyeOff, ExternalLink, Copy, Facebook, Send, Mail, MessageSquare, Twitter, TrendingUp, ShoppingBag, Globe, Check, X } from "lucide-react";
import { streamUrl, posterUrl, downloadUrl, api, API, picUrl, photoImageUrl, photoAudioUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProBadge from "@/components/ProBadge";
import { BadgeWithCelebration, CreatorBadge, BadgeCelebration } from "@/components/CreatorBadge";
import { useTopBadges } from "@/lib/badges";
import { toast } from "sonner";
import Hls from "hls.js";
import CommentDrawer from "./CommentDrawer";
import TipDialog from "./TipDialog";
import { renderMentionText } from "./MentionInput";

// Absolute URL builder for HLS playlists — hls.js needs a fully qualified
// URL so its internal loader can resolve relative segment paths correctly.
const hlsAbsoluteUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  if (/^https?:\/\//.test(relativeUrl)) return relativeUrl;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${relativeUrl.startsWith("/") ? "" : "/"}${relativeUrl}`;
};

// Module-level cache of video IDs where HLS has permanently failed
// (stall watchdog fired or manifest 404). Once a video falls back to
// progressive on this browser session, we never try HLS for it again —
// this prevents the "effect re-mount → hls.js re-init → overwrites the
// working progressive src" bug that made the fallback appear to not work.
const hlsBlacklist = new Set();

// GLOBAL cap on concurrent hls.js instances. Chrome Android's per-tab
// MediaSource / decoder pool is capped at ~6. Even with tight NEAR_WINDOW,
// rapid swipes can pile up cleanup and momentarily exceed the cap →
// "Stream Unavailable" on newer videos AND all subsequent ones (the
// cap isn't lifted until decoders actually free, which is async and can
// take seconds under memory pressure from Lottie/GIF/etc).
//
// We keep an insertion-ordered registry of live instances. Before spinning
// up a new one, force-destroy the oldest until we're back under the cap.
// Value is the destroy fn so the eviction path can run each card's own
// tear-down (destroy hls + clear <video> src + call load()).
const HLS_CONCURRENCY_CAP = 3;
const liveHlsInstances = new Map(); // videoId -> teardown()
const registerHlsInstance = (videoId, teardownFn) => {
  // Evict oldest until we have headroom.
  while (liveHlsInstances.size >= HLS_CONCURRENCY_CAP) {
    const oldest = liveHlsInstances.keys().next().value;
    if (!oldest) break;
    const fn = liveHlsInstances.get(oldest);
    liveHlsInstances.delete(oldest);
    try { fn && fn(); } catch { /* ignore */ }
  }
  liveHlsInstances.set(videoId, teardownFn);
};
const unregisterHlsInstance = (videoId) => {
  liveHlsInstances.delete(videoId);
};

// Detect Chrome on Android. This platform combo has chronic issues with
// hls.js + MediaSource Extensions: segments intermittently fail to decode
// (mid-play freezing, pixelation), the MSE decoder pool exhausts fast
// under memory pressure, and Cloudflare edge misses cascade into
// "// BUFFERING" → "Stream Unavailable" loops that ONLY reproduce on
// Android (desktop Chrome plays the same URLs cleanly).
//
// The battle-tested fix — used by TikTok, Instagram, and YouTube Shorts —
// is to skip HLS entirely on Android and serve progressive MP4 via HTTP
// byte-range. A single continuous request has no segment boundaries to
// fail on, uses Chrome Android's mature native MP4 pipeline (not the
// fragile MSE path), and recovers gracefully from packet loss without
// needing our HLS watchdog + fallback dance.
//
// Trade-off: no adaptive bitrate (we serve one MP4 rendition). Given the
// alternative is unwatchable playback on the app's primary device class,
// this is the right call. iOS Safari + desktop still use HLS.
const isAndroidChrome = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android/i.test(ua) && /Chrome|CriOS/i.test(ua) && !/Firefox/i.test(ua);
})();

// Build the iframe src for a YouTube/Vimeo embed. Always mounts muted
// (browser autoplay policy) and — critically — appends `enablejsapi=1`
// to YouTube URLs so our postMessage-based unMute later actually works.
// Without enablejsapi, YT silently drops every command message.
const buildEmbedSrc = (video) => {
  const base = video.embed_url;
  const sep = base.includes("?") ? "&" : "?";
  const isYouTube =
    video.embed_source === "youtube" || /youtube\.com|youtu\.be/.test(base);
  const params = isYouTube
    ? "autoplay=1&mute=1&playsinline=1&enablejsapi=1"
    : "autoplay=1&muted=1&playsinline=1"; // Vimeo uses `muted` (with the d)
  return `${base}${sep}${params}`;
};

// Cross-provider mute toggle for embed iframes. YouTube and Vimeo both
// expose postMessage APIs but with different envelopes:
//   YouTube: {"event":"command","func":"unMute"|"mute","args":""}
//   Vimeo:   {"method":"setVolume","value":0|1}  (also setMuted)
// We fire both — the wrong one is silently ignored by the iframe.
const sendEmbedAudioCommand = (iframeRef, video, muted) => {
  const w = iframeRef?.current?.contentWindow;
  if (!w) return;
  try {
    // YouTube
    w.postMessage(
      JSON.stringify({
        event: "command",
        func: muted ? "mute" : "unMute",
        args: "",
      }),
      "*",
    );
    // Vimeo — setMuted first (fast path), then setVolume for older embeds
    w.postMessage(
      JSON.stringify({ method: "setMuted", value: muted }),
      "*",
    );
    w.postMessage(
      JSON.stringify({ method: "setVolume", value: muted ? 0 : 1 }),
      "*",
    );
  } catch {
    /* iframe from a foreign origin still accepts postMessage */
  }
};

export default function VideoCard({ video, active, nearActive = true, onUpdate, muted, setMuted }) {
  const videoRef = useRef(null);
  const embedIframeRef = useRef(null);
  const containerRef = useRef(null);
  const mutedRef = useRef(muted);
  const [playing, setPlaying] = useState(false);
  const [liked, setLiked] = useState(video.liked);
  const [likeCount, setLikeCount] = useState(video.like_count);
  const [showComments, setShowComments] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [stealMode, setStealMode] = useState(null); // {amount, message} or null
  const [bumpLike, setBumpLike] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);
  const slideCount = video.is_photo_post ? Math.max(1, video.slide_count || 1) : 0;
  const [renderStatus, setRenderStatus] = useState(video.render_status);

  // Seek-bar state — driven by the underlying media element (works for both
  // <video> and the photo-post <audio> which share `videoRef`). Scrubbing
  // pauses the timeupdate → state sync so the thumb tracks the pointer.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const seekBarRef = useRef(null);

  // Caption expand/collapse — TikTok-style 2-line clamp with "...more" toggle.
  // We measure the caption element after mount to decide whether the "more"
  // affordance is needed (caption overflowed OR there are extra hashtags to
  // reveal beyond the visible line-clamped text).
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [captionOverflow, setCaptionOverflow] = useState(false);
  const captionRef = useRef(null);

  // Collapse the caption stack again whenever a different video mounts in
  // this card slot so the "…show more" state never leaks between videos.
  useEffect(() => {
    setCaptionExpanded(false);
  }, [video.id]);

  // Skeleton state — true once the media element can play through its current
  // buffer, i.e. we have real pixels to show. Prevents users staring at a
  // black rectangle while the object-store cold cache warms.
  // We keep the skeleton hidden for the first 800ms so fast loads never flash
  // it, and we accept `loadeddata` (first frame decoded) as "ready" instead
  // of waiting for the browser's full-buffer `canplay` — matches TikTok's
  // aggressive-start UX.
  const [mediaReady, setMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [pendingRender, setPendingRender] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);
  // Cache-busting version — bumped on each retry so the browser re-fetches
  // instead of replaying a bad cached response. Also survives the failing
  // pod's cache being stale.
  const [srcVersion, setSrcVersion] = useState(0);
  // Silent-retry guard so we don't hammer a permanently-broken video.
  const autoRetriedRef = useRef(0);   // number of silent retries attempted
  // Guard so we only report `stream_failed` to the algo once per card mount.
  const streamFailReportedRef = useRef(false);

  // --- AI voice-over translation ---
  // The user taps the Globe icon and picks a language. We call the backend,
  // it returns a URL to the dubbed .mp3, and we play that audio synced to
  // the underlying video (which we mute so the two tracks don't clash).
  //   translateLang: "es" | "hi" | null  (null = original audio)
  //   translateLoading: request in flight
  //   translatePickerOpen: language menu visible
  const [translateLang, setTranslateLang] = useState(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translatePickerOpen, setTranslatePickerOpen] = useState(false);
  const [translateUrl, setTranslateUrl] = useState(null);
  // Translated caption segments: array of {start, end, text}. Populated
  // alongside the audio URL when a dub is requested. Drives the subtitle
  // overlay below via `activeCaption`.
  const [translateSegments, setTranslateSegments] = useState([]);
  const [activeCaption, setActiveCaption] = useState("");
  const translateAudioRef = useRef(null);
  // Auto-hide the translator pill after 10s of no interaction so it stops
  // covering the video. Re-summoned by tapping the card area. A tiny "wow"
  // flip animation runs when a language is first activated (keyed on
  // `translateAnimKey`) so viewers get a confirmation cue.
  const [pillVisible, setPillVisible] = useState(true);
  const [translateAnimKey, setTranslateAnimKey] = useState(0);
  const pillHideTimerRef = useRef(null);
  // First-time discovery hint — a pulsing purple dot on the pill for the
  // user's first 3 video views ever, so viewers actually notice the new
  // translator affordance. Read from localStorage lazily on mount and
  // bumped once per unique video that shows the pill.
  const [showTranslatorHint, setShowTranslatorHint] = useState(false);


  const fmtTime = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // Detect whether the caption text actually needs the "more" affordance:
  // caption line-clamps at 2 lines, so if the natural height exceeds the
  // clamped height, we know the text was truncated. Recomputes when the
  // caption changes.
  useEffect(() => {
    const el = captionRef.current;
    if (!el) {
      setCaptionOverflow(false);
      return;
    }
    // scrollHeight = full untruncated height; clientHeight = visible clamped
    setCaptionOverflow(el.scrollHeight - el.clientHeight > 1);
  }, [video.caption, captionExpanded]);

  // Poll for slideshow render completion. Photo posts start with
  // render_status='queued' and transition to 'ready' when the MP4 is live —
  // once ready, the parent feed will treat this card as a normal video on
  // the next fetch, so we call onUpdate({...video, render_status:'ready'})
  // to swap the UI in place without a page refresh.
  useEffect(() => {
    if (!video.is_photo_post) return;
    if (renderStatus === "ready" || renderStatus === "failed") return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await api.get(`/videos/${video.id}`);
        const s = r.data?.render_status;
        if (s && s !== renderStatus) {
          setRenderStatus(s);
          if (s === "ready" || s === "failed") {
            onUpdate?.({ ...video, ...r.data });
            return;
          }
        }
      } catch { /* ignore transient errors */ }
      // Back off: 4s, 6s, 10s, 15s, 20s, then every 25s. Give up after 24 tries (~5 min).
      const delays = [4000, 6000, 10000, 15000, 20000];
      const delay = tries <= delays.length ? delays[tries - 1] : 25000;
      if (tries < 24 && !cancelled) setTimeout(poll, delay);
    };
    const kickoff = setTimeout(poll, 4000);
    return () => {
      cancelled = true;
      clearTimeout(kickoff);
    };
    // Poll driven only by identity + status; the parent may re-create `video`
    // on every re-render but that shouldn't cancel the in-flight timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id, video.is_photo_post, renderStatus]);

  // Auto-advance photo carousel every 3s while the card is active
  useEffect(() => {
    if (!video.is_photo_post || slideCount <= 1 || !active) return;
    const t = setInterval(() => {
      setSlideIdx((i) => (i + 1) % slideCount);
    }, 3000);
    return () => clearInterval(t);
  }, [active, slideCount, video.is_photo_post]);

  const nextSlide = (e) => {
    e?.stopPropagation();
    setSlideIdx((i) => (i + 1) % slideCount);
  };
  const prevSlide = (e) => {
    e?.stopPropagation();
    setSlideIdx((i) => (i - 1 + slideCount) % slideCount);
  };
  const { user } = useAuth();

  // Instant-follow "+" button on the author avatar. Local state is
  // seeded from the backend-hydrated `author.is_followed` flag and
  // flipped optimistically on click.
  const [isFollowing, setIsFollowing] = useState(!!video.author?.is_followed);
  const [followBusy, setFollowBusy] = useState(false);
  useEffect(() => {
    setIsFollowing(!!video.author?.is_followed);
  }, [video.author?.is_followed, video.author?.user_id]);

  const canShowFollowPlus =
    !!user &&
    !!video.author?.username &&
    !video.author?.is_self &&
    user.user_id !== video.author?.user_id &&
    !isFollowing;

  const handleQuickFollow = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user) {
      toast.error("Sign in to follow creators");
      return;
    }
    if (followBusy || isFollowing) return;
    setFollowBusy(true);
    setIsFollowing(true); // optimistic
    try {
      await api.post(
        `/users/${encodeURIComponent(video.author.username)}/follow`
      );
      toast.success(`Following @${video.author.username}`);
    } catch (err) {
      setIsFollowing(false);
      toast.error("Could not follow — try again");
    } finally {
      setFollowBusy(false);
    }
  };

  useEffect(() => {
    setLiked(video.liked);
    setLikeCount(video.like_count);
  }, [video.liked, video.like_count]);

  useEffect(() => {
    mutedRef.current = muted;
    const el = videoRef.current;
    if (!el) return;
    el.muted = muted;
  }, [muted]);

  // IntersectionObserver — single source of truth for which card is playing.
  // Pauses the previous card immediately and resets currentTime so its audio
  // can't keep running while another card takes focus.
  useEffect(() => {
    const root = containerRef.current?.parentElement; // scroll container
    const videoEl = videoRef.current;
    const cardEl = containerRef.current;
    if (!root || !videoEl || !cardEl) return;

    let lastVisible = null; // null = unknown, true/false = last known state
    let playPromise = Promise.resolve();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const visible = entry.intersectionRatio >= 0.6;
          if (visible === lastVisible) continue;
          lastVisible = visible;

          if (visible) {
            videoEl.muted = mutedRef.current;
            // chain off the last play/pause to avoid AbortError races
            playPromise = playPromise
              .catch(() => {})
              .then(() => {
                videoEl.currentTime = 0;
                videoEl.muted = mutedRef.current;
                return videoEl.play();
              })
              .then(() => setPlaying(true))
              .catch(() => setPlaying(false));
          } else {
            playPromise = playPromise
              .catch(() => {})
              .then(() => {
                videoEl.pause();
                videoEl.currentTime = 0;
                setPlaying(false);
              });
          }
        }
      },
      { root, threshold: [0, 0.6, 1] },
    );
    observer.observe(cardEl);
    return () => {
      observer.disconnect();
      try {
        videoEl.pause();
      } catch {
        /* ignore */
      }
    };
    // Re-run when nearActive flips so a newly-mounted <video> element gets
    // its observer attached. Without this, cards that started outside the
    // near-active window (rendered as poster <img>) never wire up the
    // play-on-visible trigger — they freeze/stay black when scrolled to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearActive]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  // Wire time/duration from the media element. Re-attach on video id change
  // (feed re-uses the same VideoCard slot for the next scroll target).
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onTime = () => {
      if (!isScrubbing) setCurrentTime(el.currentTime || 0);
    };
    const onMeta = () => setDuration(isFinite(el.duration) ? el.duration : 0);
    const onCanPlay = () => setMediaReady(true);
    const onLoadedData = () => setMediaReady(true);   // first frame decoded — enough to paint
    const onProgress = () => {
      // Any buffered range at all = we have SOMETHING to show
      try {
        if (el.buffered && el.buffered.length > 0) setMediaReady(true);
      } catch { /* ignore */ }
    };
    const onWaiting = () => setMediaReady(false);
    const onError = () => {
      // Distinguish HTTP-425 "render pending" (photo post still processing)
      // from real errors. When 425, we let the poster carry the UI until
      // the render completes and the user refreshes.
      const err = el.error;
      // MediaError.code === 4 = MEDIA_ERR_SRC_NOT_SUPPORTED, but browsers
      // don't expose HTTP status. Best signal is the src returning early
      // — we probe once with fetch to check for the specific 425.
      fetch(streamUrl(video.id), { method: "HEAD" })
        .then((r) => {
          if (r.status === 425) {
            setPendingRender(true);
            setMediaError(false);
          } else if (r.status === 410) {
            // Render permanently failed — hide the card entirely so users
            // don't stare at a broken video. Feed refreshes via onUpdate.
            onUpdate?.();
            setMediaError(true);
          } else if (r.ok && el && (el.currentSrc || "").includes("m3u8")) {
            // Safari native-HLS path failed (probably a stale hls_ready flag
            // + missing manifest on disk after redeploy). The progressive
            // /stream endpoint is 200, so silently swap over instead of
            // showing "stream unavailable".
            try {
              hlsBlacklist.add(video.id);
              const mp4 = streamUrl(video.id);
              el.src = mp4;
              el.load();
              el.play?.().catch(() => {});
              setMediaError(false);
            } catch {
              setMediaError(true);
            }
          } else {
            // Progressive src also errored. If HLS was blacklisted from a
            // stall-timeout (not a real HLS error), give HLS one more shot
            // — the stall might have been transient. This handles the case
            // where mobile network is slow enough to trigger our watchdog
            // but progressive playback then fails for other reasons.
            if (hlsBlacklist.has(video.id) && video.hls_ready) {
              hlsBlacklist.delete(video.id);
              setSrcVersion((v) => v + 1); // re-triggers effect → HLS retry
              setMediaError(false);
            } else {
              setMediaError(true);
            }
          }
        })
        .catch(() => setMediaError(true));
      void err;
    };
    // Sync any state already available (metadata may have loaded before
    // this effect attached its listeners).
    if (isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
    if (isFinite(el.currentTime)) setCurrentTime(el.currentTime);
    if (el.readyState >= 2) setMediaReady(true);   // HAVE_CURRENT_DATA is enough
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("durationchange", onMeta);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("loadeddata", onLoadedData);
    el.addEventListener("progress", onProgress);
    el.addEventListener("playing", onCanPlay);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("error", onError);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("durationchange", onMeta);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("loadeddata", onLoadedData);
      el.removeEventListener("progress", onProgress);
      el.removeEventListener("playing", onCanPlay);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id, isScrubbing, renderStatus, nearActive]);

  // Delay the skeleton reveal by 800ms — most videos are ready by then, and
  // this stops fast loads from flashing the "// buffering" label entirely.
  // Gated on `nearActive`: cards outside the near-active window render a
  // poster <img> only (no <video> element), so mediaReady/mediaError can
  // never flip away from their initial false — without this guard the
  // skeleton would show "// buffering" forever on top of a poster-only
  // card instead of just letting the poster show cleanly.
  useEffect(() => {
    // Embeds have their own iframe/link-card lifecycle — they don't drive
    // videoRef, so mediaReady never flips true and the skeleton would sit
    // on top of the YouTube iframe forever, hiding the actual player.
    if (video.is_embed || !nearActive || mediaReady || mediaError) {
      setShowSkeleton(false);
      return;
    }
    setShowSkeleton(false);
    const t = setTimeout(() => setShowSkeleton(true), 800);
    return () => clearTimeout(t);
  }, [nearActive, mediaReady, mediaError, video.id, video.is_embed]);

  // Stall watchdog — if the video sits in "// BUFFERING" for more than 12s
  // without a `canplay` OR any `progress` event (i.e. zero bytes ever
  // arrived), the connection is silently stuck. Some HTML5 <video>
  // implementations never fire `error` in that case, so our silent-retry
  // path would never trigger and users see the spinner forever. Force a
  // mediaError so the auto-retry cache-busts and re-fetches, and if that
  // ALSO stalls, the manual retry UI takes over.
  //
  // IMPORTANT: We MUST NOT trip when the browser has just chosen not to
  // preload (mobile autoplay policies, data-saver mode). In that case
  // `networkState === NETWORK_IDLE` (1) and the browser is waiting for a
  // user gesture — nothing is broken. Only kick when the browser is
  // actively LOADING (networkState === 2) and still has zero bytes.
  useEffect(() => {
    if (!nearActive || mediaReady || mediaError || pendingRender) return;
    if (video.is_photo_post) return; // photo posts have their own render path
    const t = setTimeout(() => {
      const el = videoRef.current;
      if (!el) return;
      let buffered = 0;
      try { buffered = el.buffered?.length ? el.buffered.end(0) : 0; } catch { /* ignore */ }
      if (buffered > 0) return; // bytes ARE arriving, just slowly
      // Browser deliberately not loading (autoplay blocked / paused for
      // resource savings) → NOT a stream error.
      // networkState: 0=EMPTY, 1=IDLE, 2=LOADING, 3=NO_SOURCE
      if (el.networkState !== 2) return;
      setMediaError(true);
    }, 12000);
    return () => clearTimeout(t);
  }, [nearActive, mediaReady, mediaError, pendingRender, video.id, video.is_photo_post, srcVersion]);

  // Reset media-loading state when we switch to a different video
  useEffect(() => {
    setMediaReady(false);
    setMediaError(false);
    setPendingRender(false);
    setShowSkeleton(false);
    setSrcVersion(0);
    autoRetriedRef.current = 0;
    streamFailReportedRef.current = false;
  }, [video.id]);

  // Attach the correct source to the <video> element:
  //   1. HLS ladder available AND hls.js supported → attach Hls instance
  //   2. HLS ladder AND native HLS (Safari/iOS) → set src to master.m3u8
  //   3. No HLS → fall back to progressive MP4 (srcVersion cache-buster preserved)
  //
  // We deliberately don't set the `src` attribute in JSX so both branches
  // route through the same lifecycle here — no split-brain between hls.js
  // and the browser's default loader.
  useEffect(() => {
    if (!nearActive) return;
    const el = videoRef.current;
    if (!el) return;

    // On Android Chrome, force progressive MP4 — see `isAndroidChrome`
    // banner comment above for the full rationale. Setting hlsUrl to null
    // routes this effect into the progressive-only branch below.
    const hlsUrl = (video.hls_ready && !hlsBlacklist.has(video.id) && !isAndroidChrome)
      ? hlsAbsoluteUrl(video.hls_url)
      : null;
    let hlsInstance = null;
    let stallTimer = null;

    if (hlsUrl && el.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS (Safari, iOS). No JS library needed.
      el.src = hlsUrl;
    } else if (hlsUrl && Hls.isSupported()) {
      // hls.js path — Chrome/Firefox/Android WebView.
      hlsInstance = new Hls({
        lowLatencyMode: false,
        // ~30MB buffer cap keeps memory bounded when scrubbing back-and-forth
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Start at the LOWEST rendition — first-frame latency matters more
        // than initial quality. ABR will ramp up within a segment or two.
        startLevel: 0,
        // Auto-retry knobs — help ride out flaky CDN edges without falling
        // through to the "stream unavailable" UI. Each retry doubles the
        // delay (capped at 3s) which fits Cloudflare's typical 1-2s hiccups.
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        fragLoadingMaxRetryTimeout: 3000,
        manifestLoadingMaxRetry: 4,
        manifestLoadingRetryDelay: 500,
        manifestLoadingMaxRetryTimeout: 3000,
        levelLoadingMaxRetry: 4,
        levelLoadingRetryDelay: 500,
        levelLoadingMaxRetryTimeout: 3000,
      });

      // Fallback watchdog. In production we've seen hls.js load master.m3u8
      // successfully but then get stuck (never fetches sub-playlists or
      // segments) without firing a fatal error. Behind Cloudflare + mobile
      // networks, no amount of retry config surfaces the stall.
      // If nothing plays within 3s, kill hls.js and swap to progressive
      // MP4 — every video has both endpoints available. 3s is aggressive
      // but keeps the "// BUFFERING" spinner from lingering on mobile.
      const swapToProgressive = (reason) => {
        if (!hlsInstance) return;
        console.info("[video] HLS fallback → progressive", reason, video.id);
        // Blacklist this video for the rest of the session so subsequent
        // effect re-runs (nearActive toggle, srcVersion bump, etc.) don't
        // re-init hls.js and clobber the working progressive src.
        hlsBlacklist.add(video.id);
        try { hlsInstance.destroy(); } catch { /* ignore */ }
        hlsInstance = null;
        // Free the decoder slot so a concurrent card can allocate.
        unregisterHlsInstance(video.id);
        try {
          const mp4 = srcVersion > 0
            ? `${streamUrl(video.id)}?r=${srcVersion}`
            : streamUrl(video.id);
          el.src = mp4;
          el.load();
          el.play?.().catch(() => {});
        } catch { /* ignore — media-error path will kick in */ }
      };
      const stallTimer_local = setTimeout(() => {
        // If the media element still has zero data buffered, HLS is stalled.
        if (el.readyState < 2 && (el.buffered?.length || 0) === 0) {
          swapToProgressive("stall-timeout");
        }
      }, 6000);
      stallTimer = stallTimer_local;
      hlsInstance.on(Hls.Events.FRAG_LOADED, () => clearTimeout(stallTimer_local));

      // Fatal-error recovery — without this, hls.js hangs forever on the
      // first unrecoverable network/media error. With it, we get 2 more
      // shots at rescuing playback before the "stream unavailable" UI fires.
      let hlsMediaRecoveries = 0;
      let hlsNetworkRetries = 0;
      hlsInstance.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data?.fatal) return;
        try {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // Segment / manifest download failed. If the manifest itself
            // 404s (which happens on prod after a redeploy wipes the HLS
            // disk cache), no amount of hls.js retries will help — fall
            // back to the progressive MP4 stream immediately.
            const details = data.details || "";
            const status = data?.response?.code;
            const manifestGone =
              details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
              details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
              details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR ||
              status === 404 ||
              status === 410;
            if (manifestGone || hlsNetworkRetries >= 2) {
              if (stallTimer) clearTimeout(stallTimer);
              swapToProgressive(manifestGone ? "manifest-gone" : "network-retry-exhausted");
              return;
            }
            hlsNetworkRetries += 1;
            hlsInstance.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            // Decoder or buffer error — swap decoder if we've already tried
            // a straight recover once.
            hlsMediaRecoveries += 1;
            if (hlsMediaRecoveries === 1) {
              hlsInstance.recoverMediaError();
            } else if (hlsMediaRecoveries === 2) {
              hlsInstance.swapAudioCodec();
              hlsInstance.recoverMediaError();
            } else {
              // Give up — fall through to the top-level media-error path.
              setMediaError(true);
            }
          } else {
            setMediaError(true);
          }
        } catch {
          setMediaError(true);
        }
      });

      hlsInstance.loadSource(hlsUrl);
      hlsInstance.attachMedia(el);

      // Register this instance in the global cap-registry. If we would
      // exceed HLS_CONCURRENCY_CAP, the oldest active instance is force
      // destroyed synchronously — protects us against Chrome Android's
      // MediaSource decoder cap when React cleanup lags behind mounts.
      const teardown = () => {
        try { if (hlsInstance) hlsInstance.destroy(); } catch { /* ignore */ }
        hlsInstance = null;
        try {
          el.pause();
          el.removeAttribute("src");
          el.load();
        } catch { /* ignore */ }
      };
      registerHlsInstance(video.id, teardown);
    } else {
      // No HLS ladder → progressive MP4 fallback (preserves srcVersion cache-bust).
      const mp4 = srcVersion > 0
        ? `${streamUrl(video.id)}?r=${srcVersion}`
        : streamUrl(video.id);
      el.src = mp4;
    }

    return () => {
      if (stallTimer) {
        try { clearTimeout(stallTimer); } catch { /* ignore */ }
      }
      if (hlsInstance) {
        try { hlsInstance.destroy(); } catch { /* ignore */ }
      }
      // Remove ourselves from the concurrent-instance registry so we
      // don't hold the slot open after unmount.
      unregisterHlsInstance(video.id);
      // Aggressively free the media resource on unmount. Without this,
      // mobile browsers (both iOS Safari and Android Chrome) keep the
      // MediaSource/decoder pinned to the detached video element and
      // hit their "too many concurrent media" cap after ~6-7 scrolls,
      // manifesting as buffering → "stream unavailable" on later videos.
      // Pausing + clearing src + calling load() is the canonical way to
      // tell the browser the element no longer needs its decoder.
      try {
        el.pause();
        el.removeAttribute("src");
        el.load();
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearActive, video.id, video.hls_ready, video.hls_url, srcVersion]);

  // Silent auto-retry on the FIRST media error — most stalls we see in
  // production are transient (pod cache miss, upstream 5xx, network blip).
  // Bump srcVersion once to force a fresh network fetch WITHOUT flashing
  // the "stream unavailable" UI. If the retry also fails, the error UI
  // will show and the user can hit the manual retry button.
  useEffect(() => {
    if (!mediaError) return;
    // Up to 2 silent retries — CDN edges (Cloudflare) occasionally return
    // 5xx for a single request and recover on the next. Exponential-ish
    // backoff (400ms, 900ms) so we don't hammer the origin during a real
    // outage. After both retries fail we surface the error UI.
    if (autoRetriedRef.current < 2) {
      const attempt = autoRetriedRef.current + 1;
      autoRetriedRef.current = attempt;
      const delay = attempt === 1 ? 400 : 900;
      const t = setTimeout(() => {
        setMediaError(false);
        setSrcVersion((v) => v + 1);
      }, delay);
      return () => clearTimeout(t);
    }
    // Third failure — this video is probably broken (encoding, upload
    // corruption, permanent 5xx). Tell the ranker so it can demote the
    // video for everyone. Guarded so we fire at most once per card mount.
    if (!streamFailReportedRef.current && user) {
      streamFailReportedRef.current = true;
      api.post("/algo/events", {
        video_id: video.id,
        event_type: "stream_failed",
        watched_ms: 0,
        video_duration_ms: 0,
      }).catch(() => { /* best-effort telemetry */ });
    }
  }, [mediaError, user, video.id]);

  // ---- Algorithm watch-time tracking ------------------------------------
  // Emits POST /api/algo/events every 5s while playing (`watch_progress`),
  // and on unmount / video-swap fires either `watch_complete` (>=90% or
  // rewatched) or `skip_quick` (<3s of playtime). Also detects rewatches
  // via the video's `ended` event while the card is still active.
  const watchAccumMsRef = useRef(0);
  const lastTickRef = useRef(0);
  const lastReportedMsRef = useRef(0);
  const rewatchedRef = useRef(false);
  const reportedFinalRef = useRef(false);

  useEffect(() => {
    if (!user) return; // anonymous viewers don't feed the algo
    const el = videoRef.current;
    if (!el) return;

    // Reset accumulators per video
    watchAccumMsRef.current = 0;
    lastTickRef.current = 0;
    lastReportedMsRef.current = 0;
    rewatchedRef.current = false;
    reportedFinalRef.current = false;

    let interval = null;

    const post = (event_type, watched_ms) => {
      try {
        api.post("/algo/events", {
          video_id: video.id,
          event_type,
          watched_ms: Math.max(0, Math.floor(watched_ms || 0)),
          video_duration_ms: Math.max(0, Math.floor((el.duration || 0) * 1000)),
        }).catch(() => {});
      } catch {
        /* ignore */
      }
    };

    const onPlaying = () => {
      lastTickRef.current = performance.now();
      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        const now = performance.now();
        const delta = now - lastTickRef.current;
        lastTickRef.current = now;
        if (delta > 0 && delta < 8000) watchAccumMsRef.current += delta;
        // Report every 5s incremental block
        const delta5 = watchAccumMsRef.current - lastReportedMsRef.current;
        if (delta5 >= 5000) {
          lastReportedMsRef.current = watchAccumMsRef.current;
          post("watch_progress", delta5);
        }
      }, 1000);
    };
    const onPause = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const onEnded = () => {
      // Show the BUY NOW placecard if this video has a shop link — creators
      // want a big call-to-action at the moment attention peaks.
      if (video.product_url && (video.product_image_url || video.product_image_custom_path)) {
        setEndCardOpen(true);
      }
      // Video finished at least once — count as a rewatch signal on the
      // next loop-start (loop=true means it'll auto-restart).
      if (!reportedFinalRef.current) {
        reportedFinalRef.current = true;
        post("watch_complete", watchAccumMsRef.current);
      } else {
        rewatchedRef.current = true;
        post("rewatch", watchAccumMsRef.current);
      }
    };

    // Chrome doesn't fire `ended` on looped videos, so we watch for
    // near-completion via timeupdate. Fires once per active session.
    let endCardTriggered = false;
    const onTimeUpdate = () => {
      if (endCardTriggered) return;
      if (!el.duration || !isFinite(el.duration)) return;
      if (el.duration < 1) return;  // too short to meaningfully "end"
      // Trigger within the last 300ms of the video
      if (el.duration - el.currentTime > 0.3) return;
      endCardTriggered = true;
      if (video.product_url && (video.product_image_url || video.product_image_custom_path)) {
        setEndCardOpen(true);
      }
      if (!reportedFinalRef.current) {
        reportedFinalRef.current = true;
        post("watch_complete", watchAccumMsRef.current);
      }
    };

    el.addEventListener("playing", onPlaying);
    el.addEventListener("pause", onPause);
    el.addEventListener("waiting", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      if (interval) clearInterval(interval);
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("waiting", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("timeupdate", onTimeUpdate);

      // Unmount / swap: decide final event.
      const totalMs = watchAccumMsRef.current;
      const dur = (el.duration || 0) * 1000;
      const pctWatched = dur > 0 ? totalMs / dur : 0;
      if (!reportedFinalRef.current) {
        if (totalMs < 3000 && dur > 3000) {
          post("skip_quick", totalMs);
        } else if (pctWatched >= 0.9) {
          post("watch_complete", totalMs);
        } else if (totalMs > 3000) {
          // Partial view — still a useful signal, log as watch_progress
          post("watch_progress", totalMs - lastReportedMsRef.current);
        }
      }
    };
  }, [video.id, user, nearActive]);

  // --- Translation audio sync ---
  // While a translation is active we mute the native video track and drive
  // a companion <audio> element locked to the video's clock. We nudge the
  // audio's currentTime on every timeupdate to correct any drift caused by
  // the two decoders running independently. Play/pause/seek all mirror.
  useEffect(() => {
    const video_el = videoRef.current;
    const audio_el = translateAudioRef.current;
    if (!video_el) return;
    if (!translateLang || !translateUrl || !audio_el) {
      // Native audio path — nothing to sync. Undo any leftover muting from
      // a previous translation session (parent-provided `muted` still wins).
      if (video_el) video_el.muted = !!muted;
      return;
    }

    // With a translation active the native track must be silenced so the
    // dubbed voice is the only audio the user hears.
    video_el.muted = true;
    audio_el.muted = !!muted;
    audio_el.volume = 1.0;

    const SYNC_THRESHOLD = 0.35; // seconds of drift we tolerate

    // Segment lookup — sorted by start time so we can early-out.
    const segs = (translateSegments || []).slice().sort((a, b) => (a.start || 0) - (b.start || 0));
    const pickCaptionAt = (t) => {
      if (!segs.length) return "";
      // Find the segment whose [start, end] contains t. Fall back to the
      // most recent segment whose start <= t so a small drift doesn't
      // blank the subtitle mid-sentence.
      let last = "";
      for (const s of segs) {
        if ((s.start || 0) > t + 0.05) break;
        last = s.text || "";
        if (t <= (s.end || s.start || 0) + 0.15) return s.text || "";
      }
      return last;
    };

    const syncTime = () => {
      const dur = audio_el.duration;
      let t = video_el.currentTime || 0;
      if (isFinite(dur) && dur > 0 && t > dur) t = dur;
      if (Math.abs((audio_el.currentTime || 0) - t) > SYNC_THRESHOLD) {
        try { audio_el.currentTime = t; } catch { /* ignore */ }
      }
      if (segs.length) {
        const next = pickCaptionAt(t);
        setActiveCaption((prev) => (prev === next ? prev : next));
      }
    };
    const onPlay = () => {
      syncTime();
      audio_el.play().catch(() => {});
    };
    const onPause = () => { audio_el.pause(); };
    const onSeeked = syncTime;
    const onTimeUpdate = syncTime;
    const onRateChange = () => { audio_el.playbackRate = video_el.playbackRate || 1; };

    video_el.addEventListener("play", onPlay);
    video_el.addEventListener("playing", onPlay);
    video_el.addEventListener("pause", onPause);
    video_el.addEventListener("seeked", onSeeked);
    video_el.addEventListener("timeupdate", onTimeUpdate);
    video_el.addEventListener("ratechange", onRateChange);

    // Kick off in-sync playback if the video is already playing.
    if (!video_el.paused) onPlay();

    return () => {
      video_el.removeEventListener("play", onPlay);
      video_el.removeEventListener("playing", onPlay);
      video_el.removeEventListener("pause", onPause);
      video_el.removeEventListener("seeked", onSeeked);
      video_el.removeEventListener("timeupdate", onTimeUpdate);
      video_el.removeEventListener("ratechange", onRateChange);
      try { audio_el.pause(); } catch { /* ignore */ }
      // Restore native track muting to the parent-provided value on cleanup.
      try { video_el.muted = !!muted; } catch { /* ignore */ }
    };
  }, [translateLang, translateUrl, translateSegments, muted, video.id]);

  // Request a translation from the backend (cached on the video doc, so a
  // second tap for the same lang is instant).
  const requestTranslation = async (lang) => {
    setTranslatePickerOpen(false);
    if (translateLoading) return;
    // Toggle off if same language re-selected.
    if (translateLang === lang) {
      setTranslateLang(null);
      setTranslateUrl(null);
      setTranslateSegments([]);
      setActiveCaption("");
      return;
    }
    setTranslateLoading(true);
    try {
      const r = await api.post(`/videos/${video.id}/translate`, { lang });
      const relUrl = r.data?.audio_url;
      if (!relUrl) throw new Error("no audio_url");
      const abs = relUrl.startsWith("http")
        ? relUrl
        : `${API}${relUrl.replace(/^\/api/, "")}`;
      setTranslateUrl(abs);
      setTranslateLang(lang);
      setTranslateSegments(Array.isArray(r.data?.segments) ? r.data.segments : []);
      setActiveCaption("");
      toast.success(
        r.data?.cached
          ? `Playing ${lang === "es" ? "Spanish" : lang === "zh" ? "Chinese" : "English"} translation`
          : `Translation ready — ${lang === "es" ? "Spanish" : lang === "zh" ? "Chinese" : "English"}`,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Translation failed";
      toast.error(String(msg));
    } finally {
      setTranslateLoading(false);
    }
  };

  const disableTranslation = () => {
    setTranslateLang(null);
    setTranslateUrl(null);
    setTranslateSegments([]);
    setActiveCaption("");
    setTranslatePickerOpen(false);
  };

  // Auto-hide the translator pill after 10s so it doesn't crowd the video.
  // Any interaction (tapping the pill, opening the picker, activating a
  // translation, or tapping the card area) re-summons it for another 10s.
  const showTranslatorPill = () => {
    setPillVisible(true);
    if (pillHideTimerRef.current) clearTimeout(pillHideTimerRef.current);
    pillHideTimerRef.current = setTimeout(() => {
      // Keep it visible while the picker is open or a language is active —
      // hiding those states mid-use would be jarring.
      setPillVisible((v) => {
        if (translatePickerOpen || translateLang || translateLoading) return v;
        return false;
      });
    }, 10000);
  };

  useEffect(() => {
    // Show on mount and whenever the active video swaps in.
    showTranslatorPill();
    // First-time discovery hint — pulse a small purple dot on the pill for
    // the user's first 3 video views ever. Only counts videos that:
    //   (a) qualify for the pill (real uploads, not embeds/photo posts)
    //   (b) are the currently ACTIVE card in the feed — the feed pre-renders
    //       neighbours for smooth scroll and we don't want them to burn
    //       through the 3-view budget before the user has seen any of them.
    const qualifies =
      video.storage_path !== null && !video.is_embed && !video.is_photo_post;
    if (qualifies && active) {
      try {
        const KEY = "neosync_translator_seen_count";
        const cur = parseInt(localStorage.getItem(KEY) || "0", 10) || 0;
        if (cur < 3) {
          setShowTranslatorHint(true);
          localStorage.setItem(KEY, String(cur + 1));
        } else {
          setShowTranslatorHint(false);
        }
      } catch {
        /* localStorage unavailable — silently skip the hint */
      }
    } else {
      setShowTranslatorHint(false);
    }
    return () => {
      if (pillHideTimerRef.current) clearTimeout(pillHideTimerRef.current);
    };
  }, [video.id, active]);

  // Fire the little "wow" flip animation whenever a translation activates
  // (null → es/zh). The key change re-mounts the animated wrapper so the
  // CSS keyframe replays cleanly.
  useEffect(() => {
    if (translateLang) {
      setTranslateAnimKey((k) => k + 1);
      showTranslatorPill();
    }
  }, [translateLang]);


  const seekToClientX = (clientX) => {
    const bar = seekBarRef.current;
    const el = videoRef.current;
    if (!bar || !el) return;
    const dur = isFinite(el.duration) ? el.duration : 0;
    if (!dur) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = pct * dur;
    el.currentTime = t;
    setCurrentTime(t);
  };

  const onSeekPointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsScrubbing(true);
    seekToClientX(e.clientX);
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };
  const onSeekPointerMove = (e) => {
    if (!isScrubbing) return;
    e.stopPropagation();
    seekToClientX(e.clientX);
  };
  const onSeekPointerUp = (e) => {
    if (!isScrubbing) return;
    e.stopPropagation();
    setIsScrubbing(false);
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    const el = videoRef.current;
    if (el) {
      el.muted = next;
      // Re-trigger playback inside the user-gesture so the browser allows audio
      if (!next) {
        el.play().catch(() => {});
      }
    }
    // Embeds: forward the mute state to the iframe via postMessage. This
    // works because unmuting happens inside a user-gesture event handler,
    // so browsers allow the audio to unlock without reloading the iframe.
    if (video.is_embed) {
      sendEmbedAudioCommand(embedIframeRef, video, next);
    }
  };

  const toggleLike = async () => {
    if (!user) {
      toast.error("Sign in to like videos");
      return;
    }
    setBumpLike(true);
    setTimeout(() => setBumpLike(false), 250);
    try {
      if (liked) {
        const r = await api.delete(`/videos/${video.id}/like`);
        setLiked(false);
        setLikeCount(r.data.like_count);
      } else {
        const r = await api.post(`/videos/${video.id}/like`);
        setLiked(true);
        setLikeCount(r.data.like_count);
      }
      onUpdate?.();
    } catch {
      toast.error("Could not update like");
    }
  };

  const [shareOpen, setShareOpen] = useState(false);
  // Bottom-sheet picker when the SHOP thumbnail is tapped — user chooses
  // between buying the item OR sharing the video (both options exposed
  // instead of the previous either/or behaviour).
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [deliveryInfoOpen, setDeliveryInfoOpen] = useState(false);
  const [imageZoomOpen, setImageZoomOpen] = useState(false);
  const [productImageIdx, setProductImageIdx] = useState(0);

  // Build the list of product images: prefer server-provided
  // `product_images[]` array (populated once the merchant can upload
  // multiple angles); fall back to the single `product_image_custom_path`
  // or external `product_image_url`. Always returns at least one entry
  // when there's ANY product image so we don't render an empty gallery.
  const productImages = (() => {
    const arr = Array.isArray(video.product_images) ? video.product_images.filter(Boolean) : [];
    if (arr.length) return arr;
    if (video.product_image_custom_path) return [`${API}/videos/${video.id}/product-image`];
    if (video.product_image_url) return [video.product_image_url];
    return [];
  })();
  // End-of-video "BUY NOW" placecard — appears once the video finishes
  // for videos that have a product link attached.
  const [endCardOpen, setEndCardOpen] = useState(false);
  // Exit-intent hook — if the viewer swiped away from a shop video before
  // it finished, we stashed the product data on `window.__neoSyncExitShop`.
  // The NEXT VideoCard mounts, reads it, and shows a shrink-to-corner
  // "wait — you didn't grab this?" card for 3 seconds.
  const [exitIntent, setExitIntent] = useState(null);

  // Read the exit-intent stash on mount / when this card becomes active.
  useEffect(() => {
    if (!active) return;
    try {
      const stash = window.__neoSyncExitShop;
      if (!stash) return;
      // Only show if it's a DIFFERENT video, and stashed recently (<10s ago).
      if (stash.video_id === video.id) return;
      if (Date.now() - (stash.ts || 0) > 10_000) {
        window.__neoSyncExitShop = null;
        return;
      }
      setExitIntent(stash);
      window.__neoSyncExitShop = null;
      const t = setTimeout(() => setExitIntent(null), 4000);
      return () => clearTimeout(t);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, video.id]);

  // Stash product data when this shop video becomes INACTIVE (user swiped
  // away) BEFORE reaching the end. `reportedFinalRef.current` is true only
  // after the `ended` event fired, so we use it as our "finished" signal.
  useEffect(() => {
    if (active) return; // becoming inactive
    if (!video.product_url) return;
    if (reportedFinalRef.current) return; // already watched to end
    try {
      window.__neoSyncExitShop = {
        video_id: video.id,
        product_url: video.product_url,
        product_title: video.product_title,
        product_image_url: video.product_image_url,
        product_image_custom_path: video.product_image_custom_path,
        ts: Date.now(),
      };
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  // Creator-only sharers modal + reward flow — owner can see who's
  // driving traffic to their video and tip them coins for it.
  const isVideoOwner = user && user.user_id === video.user_id;
  const [sharersOpen, setSharersOpen] = useState(false);
  const [sharersList, setSharersList] = useState([]);
  const [sharersLoading, setSharersLoading] = useState(false);
  const [rewardTarget, setRewardTarget] = useState(null); // sharer user_id
  const [rewardAmount, setRewardAmount] = useState("50"); // coins
  const [rewardBusy, setRewardBusy] = useState(false);
  // Include the sharer's username as `?ref=` so the destination page can
  // show "Shared by @X" attribution AND we can credit them on the leaderboard.
  // Share URL — points at the backend's OG-enriched share page so social
  // scrapers (Facebook / WhatsApp / Twitter / Telegram) render a real
  // thumbnail card. Real browsers hitting this URL are immediately JS-
  // redirected to `/v/{id}` so the user experience is unchanged.
  const shareUrl = user?.username
    ? `${window.location.origin}/api/share/${video.id}?ref=${encodeURIComponent(user.username)}`
    : `${window.location.origin}/api/share/${video.id}`;
  const shareText = video.caption
    ? `${video.caption.slice(0, 80)}${video.caption.length > 80 ? "…" : ""}\nMake money with matrixshopping\nClick & sign in start making $ online`
    : "Make money with matrixshopping\nClick & sign in start making $ online";

  // Robustly record a share event to the leaderboard. Fires on every
  // explicit share action (WhatsApp, Facebook, Copy Link, Native Sheet,
  // etc.), tagged with the platform for future analytics. Includes a
  // single retry after 800ms because the previous fire-and-forget code
  // silently swallowed network hiccups → users saw the leaderboard
  // number never move despite sharing. Visible toast confirms success.
  const recordShare = async (platform) => {
    if (!user) {
      toast.error("Sign in to have your shares count toward the leaderboard");
      return;
    }
    const attempt = () =>
      api.post(`/videos/${video.id}/share`, null, {
        params: { platform },
      });
    try {
      await attempt();
    } catch {
      try {
        await new Promise((r) => setTimeout(r, 800));
        await attempt();
      } catch {
        toast.error("Couldn't record that share — check your connection and try again");
        return;
      }
    }
    toast.success("+1 share recorded", { duration: 1200 });
  };

  const share = () => {
    // Sheet open is no longer a "share" on its own — count only on
    // explicit platform picks so opening + closing doesn't ghost-count.
    setShareOpen(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
      recordShare("copy_link");
    } catch {
      toast.error("Copy failed");
    }
    setShareOpen(false);
  };

  const shareVia = (target) => {
    const encU = encodeURIComponent(shareUrl);
    const encT = encodeURIComponent(shareText);
    let href = null;
    if (target === "facebook") href = `https://www.facebook.com/sharer/sharer.php?u=${encU}`;
    else if (target === "whatsapp") href = `https://wa.me/?text=${encT}%20${encU}`;
    else if (target === "twitter") href = `https://twitter.com/intent/tweet?url=${encU}&text=${encT}`;
    else if (target === "telegram") href = `https://t.me/share/url?url=${encU}&text=${encT}`;
    else if (target === "messenger") href = `https://www.facebook.com/dialog/send?link=${encU}&app_id=140586622674265&redirect_uri=${encU}`;
    else if (target === "email") href = `mailto:?subject=${encodeURIComponent("Make money with matrixshopping")}&body=${encT}%20${encU}`;
    else if (target === "sms") href = `sms:?body=${encT}%20${encU}`;
    if (!href) return;
    // Fire the share record BEFORE opening the external URL — on mobile
    // Chrome / iOS Safari the tab-swap can suspend our JS and drop an
    // in-flight fetch if it isn't already sent. `keepalive` on the
    // axios instance handles the rest.
    recordShare(target);
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
    setShareOpen(false);
  };

  const shareNative = async () => {
    try {
      await navigator.share({
        title: "Matrix Shopping",
        text: shareText,
        url: shareUrl,
      });
      // Only credit the share once the OS sheet resolves successfully
      // (user actually picked a target). AbortError below skips this.
      recordShare("native_sheet");
      setShareOpen(false);
    } catch (e) {
      // User cancelled or unsupported — leave the sheet open for a fallback tap.
      if (e?.name === "AbortError") return;
      toast.error("Native share unavailable");
    }
  };

  const buyNow = () => {
    if (!video.product_url) return;
    // Fire-and-forget click ping — same as before, but callable from any
    // affordance that opens the shop (side-rail pill AND end-of-video
    // BUY NOW placecard).
    try {
      const url = `${API}/videos/${video.id}/product-click`;
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else fetch(url, { method: "POST", keepalive: true }).catch(() => {});
    } catch { /* never block the shop navigation */ }
    window.open(video.product_url, "_blank", "noopener,noreferrer");
    setProductMenuOpen(false);
    setEndCardOpen(false);
  };

  const openProductMenu = () => setProductMenuOpen(true);

  const openSharers = async () => {
    setSharersOpen(true);
    setSharersLoading(true);
    try {
      const r = await api.get(`/videos/${video.id}/top-sharers?range_days=30&limit=25`);
      setSharersList(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load sharers");
    } finally {
      setSharersLoading(false);
    }
  };

  const sendReward = async (sharerUserId) => {
    const coins = parseInt(rewardAmount, 10);
    if (!Number.isFinite(coins) || coins < 10) {
      toast.error("Minimum reward is 10 coins ($0.10)");
      return;
    }
    setRewardBusy(true);
    try {
      const r = await api.post(`/videos/${video.id}/reward-sharer`, {
        sharer_user_id: sharerUserId,
        amount_coins: coins,
        message: null,
      });
      toast.success(`Sent ${r.data.amount_coins} coins`);
      // Refresh the sharers list to update `already_rewarded_coins`
      const r2 = await api.get(`/videos/${video.id}/top-sharers?range_days=30&limit=25`);
      setSharersList(r2.data?.items || []);
      setRewardTarget(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reward failed");
    } finally {
      setRewardBusy(false);
    }
  };

  const [saved, setSaved] = useState(!!video.viewer_saved);
  const [saveCount, setSaveCount] = useState(video.save_count || 0);
  const toggleSave = async () => {
    if (!user) {
      toast.error("Sign in to save videos");
      return;
    }
    try {
      if (saved) {
        const r = await api.delete(`/videos/${video.id}/save`);
        setSaved(false);
        setSaveCount(r.data.save_count);
      } else {
        const r = await api.post(`/videos/${video.id}/save`);
        setSaved(true);
        setSaveCount(r.data.save_count);
        toast.success("Saved");
      }
    } catch {
      toast.error("Could not update save");
    }
  };

  const notInterested = async () => {
    if (!user) {
      toast.error("Sign in to personalize your feed");
      return;
    }
    try {
      await api.post(`/videos/${video.id}/not-interested`);
      toast.success("Got it — showing you less like this");
      // Signal the feed to REMOVE this video (not just patch it). The
      // parent handler filters by { _remove: true, id }.
      onUpdate?.({ id: video.id, _remove: true });
    } catch {
      toast.error("Could not update preferences");
    }
  };

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const toastId = toast.loading("Preparing watermarked download…", {
      description: "Rendering with watermark + outro",
    });
    try {
      // 1) Kick off (or join) a background render
      const prep = await api.post(`/videos/${video.id}/download/prepare`);
      let status = prep.data.status;

      // 2) Poll until ready/failed (max ~3 minutes)
      const startedAt = Date.now();
      while (status !== "ready") {
        if (status === "failed") {
          throw new Error(prep.data.error || "Render failed");
        }
        if (Date.now() - startedAt > 180000) {
          throw new Error("Timed out waiting for render");
        }
        await new Promise((r) => setTimeout(r, 2500));
        const s = await api.get(`/videos/${video.id}/download/status`);
        status = s.data.status;
        if (status === "failed") {
          throw new Error(s.data.error || "Render failed");
        }
      }

      toast.success("Ready — downloading", { id: toastId });

      // 3) Pull the file
      const resp = await fetch(downloadUrl(video.id));
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const filename = `neosync-${video.author?.username || "video"}-${video.id.slice(0, 8)}.mp4`;

      // On mobile, the Web Share API gives the native "Save Video" sheet which
      // is more reliable than <a download> (iOS Safari often ignores it).
      const file = new File([blob], filename, { type: "video/mp4" });
      const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
      if (
        isMobile &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: filename,
            text: "Created with ios.matrixshopping.com",
          });
          toast.success("Use 'Save Video' from the share sheet", { id: toastId });
          return;
        } catch (e) {
          // User dismissed share sheet — fall through to anchor download
          if (e?.name === "AbortError") {
            toast.dismiss(toastId);
            return;
          }
        }
      }

      // Desktop / fallback: trigger normal browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Saved to your Downloads folder", { id: toastId });
    } catch (err) {
      toast.error(
        err?.message?.startsWith("Timed out")
          ? "Render is taking longer than usual — try again in a minute."
          : err?.message || "Download failed",
        { id: toastId },
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid={`video-card-${video.id}`}
      className="h-screen w-full snap-start relative bg-black flex items-center justify-center overflow-hidden"
    >
      {video.is_photo_post && renderStatus !== "ready" ? (
        <>
          {/* Slideshow render not ready yet — fall back to swipeable carousel */}
          {renderStatus && renderStatus !== "ready" && (
            <div
              data-testid={`render-status-${video.id}`}
              className="absolute top-16 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 bg-black/70 backdrop-blur-md border border-[#FF007F]/40 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/80"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF007F] animate-pulse" />
              {renderStatus === "failed"
                ? "// render.failed · swipe manually"
                : "// building.video · swipe for now"}
              {renderStatus === "failed" && isVideoOwner && (
                <button
                  type="button"
                  data-testid={`retry-render-${video.id}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await api.post(`/photos/${video.id}/render`);
                      setRenderStatus("queued");
                      toast.success("Re-rendering — this takes ~30 seconds");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Retry failed");
                    }
                  }}
                  className="ml-1 px-2 py-0.5 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black transition-colors font-mono text-[10px]"
                >
                  retry
                </button>
              )}
            </div>
          )}
          {/* Carousel: single slide if slide_count <= 1, else navigable */}
          <img
            key={slideIdx}
            src={`${photoImageUrl(video.id)}${slideCount > 1 ? `/${slideIdx}` : ""}`}
            alt={video.caption || "photo"}
            className="absolute inset-0 w-full h-full object-contain bg-black transition-opacity duration-300"
            data-testid={`photo-element-${video.id}`}
            onClick={togglePlay}
          />
          {video.has_audio_track && (
            <audio
              ref={videoRef}
              src={photoAudioUrl(video.id)}
              loop
              muted={muted}
              preload="metadata"
              data-testid={`photo-audio-${video.id}`}
            />
          )}

          {/* Carousel nav — only when >1 slide */}
          {slideCount > 1 && (
            <>
              {/* Left tap zone / arrow */}
              <button
                data-testid={`slide-prev-${video.id}`}
                onClick={prevSlide}
                className="absolute left-0 top-0 bottom-0 w-1/4 flex items-center justify-start pl-3 z-15 group/nav"
                aria-label="previous slide"
              >
                <div className="h-10 w-10 bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center opacity-0 group-hover/nav:opacity-100 transition-opacity">
                  <ChevronLeft className="h-5 w-5 text-white" strokeWidth={2} />
                </div>
              </button>
              <button
                data-testid={`slide-next-${video.id}`}
                onClick={nextSlide}
                className="absolute right-0 top-0 bottom-0 w-1/4 flex items-center justify-end pr-3 z-15 group/nav"
                aria-label="next slide"
              >
                <div className="h-10 w-10 bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center opacity-0 group-hover/nav:opacity-100 transition-opacity">
                  <ChevronRight className="h-5 w-5 text-white" strokeWidth={2} />
                </div>
              </button>

              {/* Dot indicators */}
              <div
                data-testid={`slide-dots-${video.id}`}
                className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex gap-1.5"
              >
                {Array.from({ length: slideCount }).map((_, i) => (
                  <button
                    key={`${video.id}-slide-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSlideIdx(i);
                    }}
                    data-testid={`slide-dot-${video.id}-${i}`}
                    className={`h-1 transition-all ${
                      i === slideIdx
                        ? "w-6 bg-[#FF007F] shadow-[0_0_8px_rgba(255,0,127,0.7)]"
                        : "w-3 bg-white/40 hover:bg-white/70"
                    }`}
                    aria-label={`go to slide ${i + 1}`}
                  />
                ))}
              </div>

              {/* Slide counter chip */}
              <div className="absolute top-16 right-4 z-20 px-2 py-0.5 bg-black/60 border border-white/15 font-mono text-[10px] uppercase tracking-widest text-white/80">
                {slideIdx + 1} / {slideCount}
              </div>
            </>
          )}
        </>
      ) : video.is_embed ? (
        // Embedded video from an external platform — YouTube / Vimeo get a
        // real iframe with autoplay when active; TikTok / IG / X and other
        // non-iframeable oEmbed providers get a "link card" with the
        // provider's thumbnail + a button to open the source in a new tab.
        //
        // Mobile playback quirk: iOS Safari + Chrome-mobile only autoplay
        // an iframe when `mute=1` is in the URL. If we ever swap the src
        // to `mute=0`, the iframe reloads, autoplay is blocked, the video
        // freezes on a black frame while previously-buffered audio keeps
        // running — the exact symptom users reported. So we ALWAYS mount
        // the iframe muted with `playsinline=1` and instead flip the mute
        // state at runtime via the YouTube/Vimeo postMessage APIs
        // (`enablejsapi=1` is required for YT). We gate mounting on
        // `active` so we never have >1 iframe playing at once.
        video.embed_url ? (
          active ? (
            <iframe
              ref={embedIframeRef}
              src={buildEmbedSrc(video)}
              title={video.embed_title || "Embedded video"}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              frameBorder="0"
              className="absolute inset-0 w-full h-full bg-black"
              data-testid={`embed-iframe-${video.id}`}
              onLoad={() => {
                // If the user was already unmuted before this card became
                // active (e.g. they hit Volume2 on a previous card), send
                // the unmute command as soon as the iframe finishes
                // handshaking with YouTube's JS API. Muted is the default
                // via URL so we only need to act when muted === false.
                if (!muted) sendEmbedAudioCommand(embedIframeRef, video, false);
              }}
            />
          ) : (
            // Poster placeholder — matches the aspect and keeps the swipe
            // UX buttery smooth. The moment the card becomes active the
            // iframe mounts and YouTube's player starts.
            <div
              className="absolute inset-0 w-full h-full bg-black flex items-center justify-center"
              data-testid={`embed-placeholder-${video.id}`}
            >
              {video.embed_thumbnail_url && (
                <img
                  src={video.embed_thumbnail_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                />
              )}
              <Play
                className="relative z-10 h-14 w-14 text-white drop-shadow-[0_0_12px_rgba(0,0,0,0.9)]"
                strokeWidth={1.5}
              />
            </div>
          )
        ) : (
          <a
            href={video.embed_original_url}
            target="_blank"
            rel="noreferrer"
            data-testid={`embed-linkcard-${video.id}`}
            className="absolute inset-0 flex flex-col items-center justify-center bg-black gap-4 text-center px-8"
          >
            {video.embed_thumbnail_url && (
              <img
                src={video.embed_thumbnail_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
            )}
            <div className="relative z-10 flex flex-col items-center gap-3 max-w-md">
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#00FFFF] bg-black/70 px-3 py-1 border border-[#00FFFF]/40">
                // {video.embed_source || "link"}
              </p>
              {video.embed_title && (
                <p className="font-display font-bold text-2xl text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                  {video.embed_title}
                </p>
              )}
              <span className="mt-2 bg-[#00FFFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 hover:shadow-[0_0_18px_rgba(0,255,255,0.6)] transition-shadow">
                watch on {video.embed_source || "site"} →
              </span>
            </div>
          </a>
        )
      ) : nearActive ? (
        <video
          ref={videoRef}
          poster={posterUrl(video.id)}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          playsInline
          loop
          muted={muted}
          // Only the ACTIVE card aggressively pre-fetches the full stream.
          // near-active (next-in-line) cards get `metadata` — enough to
          // pull the header + first key-frame for instant swipe playback
          // without eating bandwidth on 3-4 cards at once. On low-end
          // networks this was the difference between a snappy feed and a
          // scroll that stalls the current video while its neighbours
          // race for bytes.
          preload={active ? "auto" : "metadata"}
          onClick={togglePlay}
          data-testid={`video-element-${video.id}`}
        />
      ) : (
        // Far from the active card — render the poster image only. No
        // <video> element means no network fetch, no decoder allocation,
        // and no memory bloat. This is the fix for mobile browsers going
        // blank when they try to prep every card in the feed at once.
        <img
          src={posterUrl(video.id)}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-contain bg-black"
          data-testid={`video-poster-only-${video.id}`}
        />
      )}

      {/* Loading skeleton — shown ONLY after an 800ms grace period so fast
          loads never flash it. Dismissed the moment the browser has any
          buffered data (first frame decoded). Hidden for photo-carousel
          fallback and for embeds (both have their own visible content —
          embeds especially never fire loadeddata so the skeleton would
          sit on top of the iframe forever). */}
      {!video.is_photo_post && !video.is_embed && showSkeleton && !mediaError && (
        <div
          data-testid={`video-skeleton-${video.id}`}
          className="absolute inset-0 z-[5] bg-[#0A0A0C] overflow-hidden pointer-events-none"
        >
          <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,0,127,0.15),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(0,255,255,0.12),transparent_60%)]" />
          <div
            className="absolute inset-0 -translate-x-full animate-shimmer bg-[linear-gradient(105deg,transparent_35%,rgba(0,255,255,0.08)_50%,transparent_65%)]"
          />
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
            <div className="h-1 w-24 bg-[#FF007F]/60 shadow-[0_0_12px_rgba(255,0,127,0.6)] animate-pulse" />
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-white/50">
              // buffering
            </p>
          </div>
        </div>
      )}

      {/* Render pending — photo-post slideshow still processing on backend.
          Never returns a video stream, so we replace the <video> UI with a
          friendly "we're rendering it, come back in a bit" panel. Embeds
          are always render-ready by definition; skip. */}
      {!video.is_photo_post && !video.is_embed && pendingRender && (
        <div
          data-testid={`video-pending-${video.id}`}
          className="absolute inset-0 z-[6] bg-[#0A0A0C] flex flex-col items-center justify-center gap-3 pointer-events-none px-8 text-center"
        >
          <div className="h-1 w-16 bg-[#FF007F] shadow-[0_0_12px_rgba(255,0,127,0.6)] animate-pulse" />
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FF007F]">
            // rendering.video
          </p>
          <p className="text-white/60 text-xs max-w-xs">
            Still processing this upload. It&apos;ll be playable in a minute or two — pull to refresh soon.
          </p>
        </div>
      )}

      {/* Upload-status overlay — server tells us WHY a video isn't ready
          (processing / retrying / failed) so we can replace the generic
          "STREAM UNAVAILABLE" mystery with an actionable message. Owners
          + admins get a full-screen overlay so they can act on their own
          content. Regular viewers get a minimal corner pill and can just
          scroll past. Ready videos get nothing. Photo posts and embeds
          bypass this entirely (they don't have an HLS pipeline). */}
      {!video.is_photo_post && !video.is_embed && video.upload_status &&
       video.upload_status.state !== "ready" && (
        (() => {
          const st = video.upload_status;
          const isOwner = !!video.author?.is_self;
          const isAdmin = !!user?.is_admin;
          const stateColors = {
            processing: { bar: "#39FF14", text: "#39FF14", shadow: "rgba(57,255,20,0.55)", label: "// processing" },
            playable_mp4: { bar: "#00E5FF", text: "#00E5FF", shadow: "rgba(0,229,255,0.55)", label: "// upscaling" },
            retrying: { bar: "#FFCC00", text: "#FFCC00", shadow: "rgba(255,204,0,0.55)", label: "// retrying" },
            failed: { bar: "#FF3B30", text: "#FF3B30", shadow: "rgba(255,59,48,0.55)", label: "// failed" },
          }[st.state] || { bar: "#39FF14", text: "#39FF14", shadow: "rgba(57,255,20,0.55)", label: "// processing" };
          const fmtElapsed = (s) => {
            if (s == null) return null;
            if (s < 60) return `${s}s ago`;
            if (s < 3600) return `${Math.floor(s / 60)}m ago`;
            return `${Math.floor(s / 3600)}h ago`;
          };
          // For `playable_mp4`, the video actually plays — so ONLY render
          // a corner badge (never the full overlay) since blocking a
          // playing video would be worse than showing standard quality.
          const forceCornerOnly = st.state === "playable_mp4";
          if (!forceCornerOnly && (isOwner || isAdmin)) {
            return (
              <div
                data-testid={`video-status-${video.id}`}
                className="absolute inset-0 z-[7] bg-[#0A0A0C]/95 flex flex-col items-center justify-center gap-3 px-6 text-center pointer-events-none"
              >
                <div
                  className="h-1 w-16 animate-pulse"
                  style={{ backgroundColor: stateColors.bar, boxShadow: `0 0 12px ${stateColors.shadow}` }}
                />
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.35em]"
                  style={{ color: stateColors.text }}
                >
                  {stateColors.label}
                </p>
                {st.message && (
                  <p className="text-white text-sm max-w-[280px]">{st.message}</p>
                )}
                {st.hint && (
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 max-w-[280px]">
                    {st.hint}
                  </p>
                )}
                <div className="flex items-center gap-3 font-mono text-[9px] uppercase tracking-widest text-white/40">
                  {st.elapsed_seconds != null && <span>uploaded {fmtElapsed(st.elapsed_seconds)}</span>}
                  {st.retry_attempts > 0 && <span>· retries {st.retry_attempts}/3</span>}
                  {(isAdmin && !isOwner) && <span>· admin view</span>}
                </div>
              </div>
            );
          }
          // Viewer / playable_mp4 path — small, unobtrusive corner pill
          // so we tell them why quality might feel off without blocking
          // playback.
          return (
            <div
              data-testid={`video-status-pill-${video.id}`}
              className="absolute top-3 left-3 z-[7] pointer-events-none flex items-center gap-1.5 px-2 py-1 bg-black/70 backdrop-blur-sm border"
              style={{ borderColor: `${stateColors.bar}55` }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ backgroundColor: stateColors.bar, boxShadow: `0 0 6px ${stateColors.shadow}` }}
              />
              <span
                className="font-mono text-[9px] uppercase tracking-widest"
                style={{ color: stateColors.text }}
              >
                {st.state === "playable_mp4" ? "SD" :
                 st.state === "failed" ? "unavailable" :
                 st.state === "retrying" ? "retrying" : "processing"}
              </span>
            </div>
          );
        })()
      )}

      {/* Error state — replaces the skeleton if the video failed to load
          (usually a stream 5xx from the origin). Embeds never hit the
          stream endpoint (iframe fetches directly from YouTube/Vimeo) so
          `mediaError` is meaningless for them; skip. Also skip when the
          server already told us WHY playback is failing via
          `upload_status` — no point showing a generic "stream
          unavailable" on top of a "processing · 2 min left" overlay. */}
      {!video.is_photo_post && !video.is_embed && mediaError &&
       (!video.upload_status || video.upload_status.state === "ready") && (
        <div
          data-testid={`video-error-${video.id}`}
          className="absolute inset-0 z-[6] bg-[#0A0A0C] flex flex-col items-center justify-center gap-4 pointer-events-none px-6 text-center"
        >
          <div className="h-1 w-16 bg-[#FF3B30] shadow-[0_0_12px_rgba(255,59,48,0.6)]" />
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FF3B30]">
            // stream unavailable
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 max-w-[240px]">
            couldn&apos;t reach the stream — hit retry, or scroll to the next video.
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMediaError(false);
              setMediaReady(false);
              setShowSkeleton(false);
              // Bump srcVersion → forces a fresh network fetch and dodges
              // any stale intermediate cache that might have the bad
              // response memoized.
              setSrcVersion((v) => v + 1);
              // Give React one tick to re-render the <video> with the new
              // src before nudging play.
              setTimeout(() => {
                const el = videoRef.current;
                if (!el) return;
                try { el.load(); } catch { /* ignore */ }
                el.play?.().catch(() => {});
              }, 30);
            }}
            className="pointer-events-auto font-mono text-[11px] uppercase tracking-widest text-black bg-[#39FF14] hover:shadow-[0_0_16px_rgba(57,255,20,0.55)] px-4 py-2 font-bold transition-shadow"
            data-testid={`video-retry-${video.id}`}
          >
            retry stream
          </button>
        </div>
      )}

      {/* play overlay — hidden while the photo-carousel fallback is visible
          and for embeds (the iframe has YouTube/Vimeo's native controls,
          our overlay would just block them and never do anything useful
          since togglePlay only manipulates the local videoRef). */}
      {!playing && active && !video.is_embed && !(video.is_photo_post && renderStatus !== "ready") && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 z-10"
          data-testid={`play-overlay-${video.id}`}
        >
          <div className="h-20 w-20 bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center">
            <Play className="h-9 w-9 text-white" fill="white" />
          </div>
        </button>
      )}

      {/* view count + trending chip — top left, side-by-side */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
        <div
          data-testid={`view-count-${video.id}`}
          className="px-3 h-10 bg-black/60 backdrop-blur-md border border-white/15 flex items-center gap-2 text-white"
        >
          <Eye className="h-4 w-4 text-[#00FFFF]" strokeWidth={1.8} />
          <span className="font-mono text-xs tracking-wide">
            {(video.displayed_view_count ?? video.view_count ?? 0).toLocaleString()}
            <span className="ml-1 text-white/40 uppercase tracking-widest text-[10px]">
              views
            </span>
          </span>
        </div>

        {/* Trending chip — surfaces when the backend's share-boost
            multiplier is riding at 1.25x+ (i.e. ≥ 5% share-rate). The
            same threshold that actually lifts the video in the ranker,
            so the badge is honest social proof rather than a marketing
            decoration. */}
        {video.is_trending && (
          <div
            data-testid={`trending-badge-${video.id}`}
            className="h-10 px-2.5 bg-black/60 backdrop-blur-md border border-[#FF007F]/60 shadow-[0_0_18px_rgba(255,0,127,0.45)] flex items-center gap-1 text-[#FF007F] animate-trending-pulse"
          >
            <TrendingUp className="h-4 w-4" strokeWidth={2.2} />
            <span className="font-mono text-[10px] uppercase tracking-widest font-bold">
              trending
            </span>
          </div>
        )}
      </div>

      {/* AI voice-over dub — compact pill under the view counter (top-left)
          so it doesn't crowd the right-hand action rail. Only shown for real
          uploads (embeds + photo posts have no storage_path). Auto-hides
          after 10s of inactivity — re-summoned by tapping this hit-zone. */}
      {video.storage_path !== null && !video.is_embed && !video.is_photo_post && (
        <div
          className="absolute top-16 left-4 z-20"
          onPointerEnter={showTranslatorPill}
          onClickCapture={showTranslatorPill}
        >
          <div
            className={`transition-all duration-500 ${
              pillVisible
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-1 pointer-events-none"
            }`}
          >
            <button
              key={translateAnimKey}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                showTranslatorPill();
                setShowTranslatorHint(false);
                if (translateLoading) return;
                if (translateLang) {
                  disableTranslation();
                } else {
                  setTranslatePickerOpen((v) => !v);
                }
              }}
              disabled={translateLoading}
              data-testid={`translate-btn-${video.id}`}
              title={translateLang ? "Back to original audio" : "AI voice-over translator"}
              style={{
                animation: translateLang
                  ? "translatorFlipIn 650ms cubic-bezier(0.22,1,0.36,1)"
                  : undefined,
              }}
              className={`relative h-9 px-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-md border font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-60 ${
                translateLang
                  ? "border-[#39FF14] text-[#39FF14] shadow-[0_0_14px_rgba(57,255,20,0.5)]"
                  : "border-white/15 text-white hover:border-[#39FF14]/60 hover:text-[#39FF14]"
              }`}
            >
              {translateLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
              ) : (
                <Globe className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              <span>
                {translateLoading
                  ? "translating"
                  : translateLang === "es"
                  ? "ES"
                  : translateLang === "zh"
                  ? "ZH"
                  : translateLang === "en"
                  ? "EN"
                  : "translator"}
              </span>
              {showTranslatorHint && !translateLang && !translateLoading && (
                <span
                  data-testid={`translate-hint-${video.id}`}
                  className="absolute -top-1 -right-1 flex h-2.5 w-2.5"
                  aria-hidden="true"
                >
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#B537F2] opacity-70 animate-translator-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#B537F2] shadow-[0_0_8px_rgba(181,55,242,0.9)]" />
                </span>
              )}
            </button>
            {translatePickerOpen && !translateLoading && (
              <div
                data-testid={`translate-picker-${video.id}`}
                className="mt-2 w-44 rounded-md bg-black/90 backdrop-blur-md border border-[#39FF14]/40 shadow-[0_0_18px_rgba(57,255,20,0.35)] py-1"
                onClick={(e) => { e.stopPropagation(); showTranslatorPill(); }}
              >
                <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10 flex items-center justify-between">
                  <span>AI Translator</span>
                  {video.source_lang && (
                    <span
                      data-testid={`translate-source-lang-${video.id}`}
                      className="text-[#39FF14]/80"
                      title="Spoken language detected from the audio"
                    >
                      spoken: {video.source_lang.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </div>
                {(() => {
                  // Reorder the picker so the most useful target (i.e. the
                  // OPPOSITE of the video's spoken language) is first.
                  // Whisper caches the detected source in `video.source_lang`
                  // after the first translation runs — until then we fall
                  // back to the default English-source assumption.
                  const src = (video.source_lang || "").toLowerCase().slice(0, 2);
                  const all = [
                    { code: "es", label: "Spanish", flag: "ES" },
                    { code: "zh", label: "Chinese", flag: "ZH" },
                    { code: "en", label: "English", flag: "EN" },
                  ];
                  // Preferred first pick per source lang (natural counterpart).
                  const preferredFirst = {
                    en: "es",
                    es: "en",
                    zh: "en",
                  }[src];
                  if (preferredFirst) {
                    all.sort((a, b) => {
                      if (a.code === preferredFirst) return -1;
                      if (b.code === preferredFirst) return 1;
                      // De-prioritize the source language (translating a
                      // Spanish video into Spanish is rarely useful).
                      if (a.code === src) return 1;
                      if (b.code === src) return -1;
                      return 0;
                    });
                  }
                  return all;
                })().map((opt) => (
                  <button
                    key={opt.code}
                    type="button"
                    data-testid={`translate-pick-${opt.code}-${video.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      requestTranslation(opt.code);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-white text-sm hover:bg-[#39FF14]/10"
                  >
                    <span className="inline-flex h-6 w-8 items-center justify-center rounded bg-[#39FF14]/15 text-[#39FF14] text-[10px] font-mono font-bold">
                      {opt.flag}
                    </span>
                    <span className="flex-1">{opt.label}</span>
                    {translateLang === opt.code && (
                      <Check className="h-4 w-4 text-[#39FF14]" strokeWidth={2.4} />
                    )}
                  </button>
                ))}
                {translateLang && (
                  <button
                    type="button"
                    onClick={disableTranslation}
                    data-testid={`translate-off-${video.id}`}
                    className="w-full px-3 py-2 text-left text-white/70 text-xs hover:bg-white/5 border-t border-white/10 uppercase font-mono tracking-widest"
                  >
                    Original audio
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top boost crown banner — hovers top-center; only shown for ≥$5 tips */}
      {video.top_boost && (
        <div
          data-testid={`top-boost-banner-${video.id}`}
          className="absolute top-4 left-1/2 -translate-x-1/2 z-20 max-w-[80%] flex items-stretch"
        >
          <Link
            to={video.top_boost.username ? `/u/${video.top_boost.username}` : "#"}
            onClick={(e) => {
              if (!video.top_boost.username) e.preventDefault();
            }}
            data-testid={`top-boost-link-${video.id}`}
            className="flex items-center gap-2 px-3 h-10 backdrop-blur-md border transition-all"
            style={{
              background: "linear-gradient(90deg, rgba(255,184,0,0.18), rgba(255,0,127,0.18))",
              borderColor: "rgba(255,184,0,0.55)",
              boxShadow: "0 0 22px rgba(255,184,0,0.35)",
            }}
          >
            <Crown className="h-4 w-4 text-[#FFB800] flex-shrink-0" strokeWidth={2} fill="#FFB800" />
            {video.top_boost.picture ? (
              <img
                src={picUrl(video.top_boost.picture)}
                alt=""
                className="h-6 w-6 object-cover border border-[#FFB800]/60 flex-shrink-0"
              />
            ) : (
              <div className="h-6 w-6 bg-[#FF007F]/25 flex items-center justify-center text-[#FF007F] font-display font-bold text-[10px] border border-[#FF007F]/60 flex-shrink-0">
                {(video.top_boost.name || "?")[0]?.toUpperCase()}
              </div>
            )}
            <span
              data-testid={`top-boost-name-${video.id}`}
              className="font-display font-bold text-white text-xs truncate max-w-[90px] sm:max-w-[180px]"
            >
              {video.top_boost.name || "Anonymous"}
            </span>
            <span
              className="font-display font-black text-sm flex-shrink-0"
              style={{ color: "#FFB800", textShadow: "0 0 10px rgba(255,184,0,0.6)" }}
            >
              ${(video.top_boost.amount_cents / 100).toFixed(0)}
            </span>
            <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-widest text-white/50 flex-shrink-0">
              // top.boost
            </span>
          </Link>
          {/* STEAL button — only if viewer is not the creator + not the current top-booster */}
          {(!user || (user.user_id !== video.author?.user_id && user.user_id !== video.top_boost.user_id)) && (
            <button
              data-testid={`steal-crown-btn-${video.id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (!user) {
                  toast.error("Sign in to steal the crown");
                  return;
                }
                const stealCents = (video.top_boost.amount_cents || 0) + 100;
                setStealMode({
                  amount: (stealCents / 100).toFixed(2),
                  message: `stole the crown from ${video.top_boost.name || "the last whale"}`,
                });
                setShowTip(true);
              }}
              className="ml-[-1px] px-3 h-10 border border-[#FF007F] bg-[#FF007F]/15 hover:bg-[#FF007F]/25 flex items-center gap-1.5 transition-all group/steal"
              style={{ boxShadow: "0 0 14px rgba(255,0,127,0.35)" }}
              title="Boost $1 more to claim the crown"
            >
              <Zap
                className="h-3 w-3 text-[#FF007F] group-hover/steal:scale-110 transition-transform"
                fill="#FF007F"
                strokeWidth={0}
              />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF007F] font-bold">
                steal
              </span>
              <span className="font-display font-black text-xs text-white">
                ${((video.top_boost.amount_cents + 100) / 100).toFixed(0)}
              </span>
            </button>
          )}
        </div>
      )}

      {/* mute toggle — visible for both native videos and embeds. For
          embeds, `toggleMute` also fires a postMessage to the YouTube /
          Vimeo iframe (via `sendEmbedAudioCommand`) so the iframe unmutes
          in-place without reloading. YouTube's own baked-in unmute button
          is fine but often gets tucked away on mobile; giving users the
          app's own button matches the muscle memory they have on native
          uploads. */}
      <button
        data-testid="mute-toggle-btn"
        onClick={toggleMute}
        className={`absolute top-4 right-4 z-20 px-3 h-10 bg-black/60 backdrop-blur-md border flex items-center gap-2 text-white transition-all ${
          muted
            ? "border-[#39FF14]/60 text-[#39FF14] shadow-[0_0_15px_rgba(57,255,20,0.35)]"
            : "border-white/10 hover:border-[#00FFFF]/60"
        }`}
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        <span className="font-mono text-[10px] uppercase tracking-widest">
          {muted ? "tap for sound" : "on"}
        </span>
      </button>

      {/* Not interested — negative interest signal for the algorithm. Owner
          of the video doesn't see this on their own posts (would be weird). */}
      {user && video.user_id !== user.user_id && (
        <button
          onClick={notInterested}
          data-testid={`not-interested-btn-${video.id}`}
          title="Show me less like this"
          className="absolute top-16 right-4 z-20 h-8 w-8 bg-black/60 backdrop-blur-md border border-white/10 hover:border-[#FF3B30]/60 hover:text-[#FF3B30] flex items-center justify-center text-white/70 transition-colors"
        >
          <EyeOff className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      )}

      {/* Translated subtitle overlay — shown only when a dub is active
          and we have segments for the current time. Sits above the caption
          block so it doesn't collide with the seek bar / creator info. */}
      {translateLang && activeCaption && (
        <div
          data-testid={`translation-caption-${video.id}`}
          className="absolute left-4 right-20 sm:right-24 bottom-[calc(1rem+80px)] sm:bottom-[calc(1rem+120px)] z-20 flex justify-center pointer-events-none"
        >
          <div className="max-w-[95%] px-3 py-1.5 bg-black/75 backdrop-blur-md border border-[#39FF14]/40 rounded-md shadow-[0_0_18px_rgba(57,255,20,0.25)]">
            <p className="text-white text-sm sm:text-base leading-snug text-center font-medium drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
              {activeCaption}
            </p>
          </div>
        </div>
      )}

      {/* bottom overlay */}
      <div className="absolute bottom-0 left-0 right-20 sm:right-24 p-4 pb-24 sm:pb-8 bg-gradient-to-t from-black/90 via-black/50 to-transparent z-10">
        {/* interactive seek bar — sits at the top of the caption block so it
            adapts to variable caption height. Hidden while the photo-carousel
            fallback is visible (pre-render). */}
        {!(video.is_photo_post && renderStatus !== "ready") && (
          <div
            data-testid={`seek-container-${video.id}`}
            className="mb-3 flex items-center gap-2.5 font-mono text-[10px] tabular-nums text-white/80 select-none"
          >
            <span
              data-testid={`seek-current-${video.id}`}
              className="min-w-[34px] text-[#00FFFF]"
            >
              {fmtTime(currentTime)}
            </span>
            <div
              ref={seekBarRef}
              data-testid={`seek-bar-${video.id}`}
              onPointerDown={onSeekPointerDown}
              onPointerMove={onSeekPointerMove}
              onPointerUp={onSeekPointerUp}
              onPointerCancel={onSeekPointerUp}
              onClick={(e) => e.stopPropagation()}
              role="slider"
              aria-label="seek video"
              aria-valuemin={0}
              aria-valuemax={Math.floor(duration) || 0}
              aria-valuenow={Math.floor(currentTime) || 0}
              tabIndex={-1}
              className="relative flex-1 h-5 flex items-center cursor-pointer touch-none group/seek"
            >
              {/* track */}
              <div className="absolute left-0 right-0 h-[3px] bg-white/15 group-hover/seek:h-[5px] transition-[height]" />
              {/* played fill */}
              <div
                className="absolute left-0 h-[3px] bg-[#FF007F] shadow-[0_0_10px_rgba(255,0,127,0.65)] group-hover/seek:h-[5px] transition-[height]"
                style={{
                  width: `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%`,
                }}
              />
              {/* thumb */}
              <div
                className={`absolute h-3 w-3 -translate-x-1/2 rounded-full bg-[#00FFFF] shadow-[0_0_12px_rgba(0,255,255,0.8)] border border-white transition-transform ${
                  isScrubbing ? "scale-150" : "scale-0 group-hover/seek:scale-100"
                }`}
                style={{
                  left: `${duration ? Math.min(100, (currentTime / duration) * 100) : 0}%`,
                }}
              />
            </div>
            <span
              data-testid={`seek-duration-${video.id}`}
              className="min-w-[34px] text-right text-white/45"
            >
              {fmtTime(duration)}
            </span>
          </div>
        )}
        <Link
          to={`/u/${video.author?.username}`}
          className="flex items-center gap-3 mb-3 group/author"
          data-testid={`video-author-link-${video.id}`}
        >
          <div className="relative h-10 w-10 shrink-0">
            <AuthorAvatarWithBadge
              authorId={video.author?.user_id}
              username={video.author?.username}
              picture={video.author?.picture}
              name={video.author?.name}
            />
            {canShowFollowPlus && (
              <button
                type="button"
                onClick={handleQuickFollow}
                disabled={followBusy}
                data-testid={`follow-plus-btn-${video.id}`}
                aria-label={`Follow @${video.author?.username}`}
                title={`Follow @${video.author?.username}`}
                className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 h-5 w-5 rounded-full bg-[#FF007F] text-white flex items-center justify-center text-[13px] font-bold leading-none shadow-[0_0_10px_rgba(255,0,127,0.75)] ring-2 ring-black hover:scale-110 active:scale-95 transition-transform disabled:opacity-60"
              >
                +
              </button>
            )}
          </div>
          <div>
            <p className="font-semibold text-white text-sm group-hover/author:text-[#39FF14] transition-colors flex items-center gap-1.5">
              <span className="truncate">{video.author?.name || `@${video.author?.username}`}</span>
              <ProBadge isPro={video.author?.is_pro} size="xs" />
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
              @{video.author?.username} · {new Date(video.created_at).toLocaleDateString()}
            </p>
          </div>
        </Link>
        {captionExpanded && video.caption && (
          <p
            ref={captionRef}
            data-testid={`caption-text-${video.id}`}
            className="text-white text-sm leading-relaxed max-w-md break-words mb-2"
          >
            {renderMentionText(video.caption)}
          </p>
        )}
        {/* more/less toggle — collapsed by default so the feed stays clean
            (author + sound chip only, matching the compact layout). Reveals
            the full caption, hashtags, boosted strip and boost message. */}
        {(video.caption || video.hashtags?.length > 0 ||
          video.tips_total_cents > 0 || video.latest_boost_message?.message) && (
          <button
            type="button"
            data-testid={`caption-toggle-${video.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setCaptionExpanded((v) => !v);
            }}
            className="mb-2 font-mono text-[11px] uppercase tracking-widest text-white/50 hover:text-[#00FFFF] transition-colors"
          >
            {captionExpanded ? "show less" : "…show more"}
          </button>
        )}
        {video.music_track_id && (
          <Link
            to={`/sound/${encodeURIComponent(video.music_track_id)}`}
            data-testid={`video-sound-chip-${video.id}`}
            className="inline-flex items-center gap-1.5 border border-[#FF007F]/40 hover:border-[#FF007F] bg-[#FF007F]/5 hover:bg-[#FF007F]/10 text-white/80 hover:text-white px-2.5 py-1 mb-3 font-mono text-[10px] uppercase tracking-widest transition-all max-w-md"
          >
            <span className="text-[#FF007F]">♪</span>
            <span className="truncate">
              {video.music_track_title}
              {video.music_track_artist ? ` · ${video.music_track_artist}` : ""}
            </span>
          </Link>
        )}
        {captionExpanded && video.hashtags?.length > 0 && (
          <div
            data-testid={`caption-hashtags-${video.id}`}
            className="flex flex-wrap gap-2 mb-3"
          >
            {video.hashtags.map((tag) => (
              <Link
                key={tag}
                to={`/t/${tag}`}
                className="font-mono text-xs text-[#00FFFF] hover:text-[#39FF14] transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}
        {/* boosted strip — collapsed behind "…show more" */}
        {captionExpanded && video.tips_total_cents > 0 && (
          <div
            data-testid={`boosted-strip-${video.id}`}
            className="flex items-center gap-2 mb-3 max-w-md"
          >
            <div className="flex -space-x-2">
              {video.recent_tippers?.slice(0, 3).map((t, i) => (
                <div
                  key={i}
                  className="h-6 w-6 rounded-full bg-[#FF007F]/30 border-2 border-black flex items-center justify-center overflow-hidden"
                  title={t.name}
                >
                  {t.picture ? (
                    <img src={picUrl(t.picture)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-display font-bold text-[#FF007F]">
                      {(t.name || "?")[0]?.toUpperCase()}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <span className="font-mono text-[11px] uppercase tracking-widest">
              <span className="text-[#FF007F] font-bold">
                ${(video.tips_total_cents / 100).toFixed(2)}
              </span>
              <span className="text-white/50"> boosted</span>
            </span>
          </div>
        )}
        {/* latest boost message quote — collapsed behind "…show more" */}
        {captionExpanded && video.latest_boost_message?.message && (
          <div
            data-testid={`boost-message-${video.id}`}
            className="mb-3 max-w-md p-2.5 border-l-2 border-[#FF007F] bg-[#FF007F]/10 backdrop-blur-sm"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-[#FF007F]/80">
                {video.latest_boost_message.name || "Anonymous"}
              </span>
              <span className="font-display font-black text-[10px] text-[#FF007F]">
                +${(video.latest_boost_message.amount_cents / 100).toFixed(2)}
              </span>
            </div>
            <p className="text-white/90 text-xs italic leading-snug line-clamp-2 break-words">
              “{video.latest_boost_message.message}”
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 text-white/60 text-xs font-mono">
          <Music className="h-3 w-3" />
          <span>original sound · {video.author?.name || `@${video.author?.username}`}</span>
        </div>
      </div>

      {/* interaction rail */}
      <div className="absolute right-3 bottom-28 sm:bottom-8 flex flex-col gap-3 items-center z-10">
        <button
          onClick={toggleLike}
          data-testid={`like-btn-${video.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div
            className={`h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border flex items-center justify-center transition-all ${
              liked
                ? "border-[#FF007F] text-[#FF007F] shadow-[0_0_20px_rgba(255,0,127,0.6)]"
                : "border-white/10 text-white hover:border-white/30"
            } ${bumpLike ? "scale-125" : "scale-100"}`}
          >
            <Heart className="h-4 w-4" fill={liked ? "#FF007F" : "none"} strokeWidth={liked ? 0 : 1.8} />
          </div>
          <span className="text-white text-[11px] font-semibold">{likeCount}</span>
        </button>

        <button
          onClick={() => setShowComments(true)}
          data-testid={`comments-btn-${video.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div className="h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:border-[#00FFFF]/60 hover:text-[#00FFFF] transition-all">
            <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
          </div>
          <span className="text-white text-[11px] font-semibold">
            {video.comment_count}
          </span>
        </button>

        {video.product_url && (video.product_image_url || video.product_image_custom_path) ? (
          <button
            type="button"
            data-testid={`product-link-${video.id}`}
            onClick={(e) => { e.stopPropagation(); openProductMenu(); }}
            className="flex flex-col items-center gap-1"
            title={video.product_title || "Shop or share"}
          >
            {/* Two labels on the ONE shop tile: SHARE on top so viewers
                immediately clock the earn-by-sharing hook, SHOP on the
                bottom so they know a tap can also buy. Tapping the tile
                opens the action-picker where they pick which. */}
            <span
              data-testid={`product-link-share-label-${video.id}`}
              className="text-[#39FF14] text-xs font-mono uppercase tracking-widest drop-shadow-[0_0_6px_rgba(57,255,20,0.7)]"
            >
              share
            </span>
            {/* 5-second attention pulse — the shop tile FLASHES between
                5s and 9s of playback (four seconds ≈ two full pulse
                cycles) so first-time viewers can't miss the shop hook.
                Uses `video.currentTime` not wall-clock so pausing pauses
                the flash. Only fires while `active` (current card in feed). */}
            <div
              className={`h-12 w-12 rounded-lg bg-black/50 backdrop-blur-md border overflow-hidden relative transition-all ${
                active && currentTime >= 5 && currentTime < 9
                  ? "border-[#FF9500] shadow-[0_0_30px_rgba(255,149,0,1)] scale-125 animate-pulse ring-2 ring-[#FF9500]"
                  : "border-[#FF9500]/60 hover:border-[#FF9500] hover:shadow-[0_0_18px_rgba(255,149,0,0.5)]"
              }`}
            >
              <img
                src={
                  video.product_image_custom_path
                    ? `${API}/videos/${video.id}/product-image`
                    : video.product_image_url
                }
                alt={video.product_title || "product"}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = "0"; }}
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors">
                <ExternalLink className="h-3.5 w-3.5 text-[#FF9500] opacity-0 hover:opacity-100" strokeWidth={2} />
              </div>
            </div>
            <span className="text-white text-xs font-mono uppercase">shop</span>
          </button>
        ) : (
          <button
            onClick={share}
            data-testid={`share-btn-${video.id}`}
            className="flex flex-col items-center gap-1"
          >
            <div className="h-12 w-12 rounded-full bg-black/50 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:border-[#39FF14]/60 hover:text-[#39FF14] transition-all">
              <Share2 className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <span className="text-white text-xs font-mono uppercase">share</span>
          </button>
        )}
        {isVideoOwner && (
          <button
            onClick={openSharers}
            data-testid={`open-sharers-${video.id}`}
            className="flex flex-col items-center gap-1"
            title="See who's sharing this video"
          >
            <div className="h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border border-[#FFD700]/50 flex items-center justify-center text-[#FFD700] hover:border-[#FFD700] hover:shadow-[0_0_16px_rgba(255,215,0,0.5)] transition-all">
              <TrendingUp className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-white/70 text-[9px] font-mono uppercase tracking-widest">sharers</span>
          </button>
        )}

        <button
          onClick={toggleSave}
          data-testid={`save-btn-${video.id}`}
          className="flex flex-col items-center gap-0.5"
        >
          <div
            className={`h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border flex items-center justify-center transition-all ${
              saved
                ? "border-[#00FFFF] text-[#00FFFF] shadow-[0_0_18px_rgba(0,255,255,0.55)]"
                : "border-white/10 text-white hover:border-white/30"
            }`}
          >
            <Bookmark
              className="h-4 w-4"
              fill={saved ? "#00FFFF" : "none"}
              strokeWidth={saved ? 0 : 1.8}
            />
          </div>
          <span className="text-white text-[11px] font-semibold">{saveCount}</span>
        </button>

        <button
          onClick={() => setShowTip(true)}
          data-testid={`boost-btn-${video.id}`}
          className="flex flex-col items-center gap-0.5 group"
        >
          <div className="h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border border-[#FF007F]/40 flex items-center justify-center text-[#FF007F] hover:border-[#FF007F] hover:shadow-[0_0_20px_rgba(255,0,127,0.6)] transition-all group-hover:scale-110">
            <Zap className="h-4 w-4" fill="#FF007F" strokeWidth={0} />
          </div>
          <span className="text-white text-[10px] font-mono uppercase">boost</span>
        </button>

        <button
          onClick={download}
          disabled={downloading}
          data-testid={`download-btn-${video.id}`}
          className="flex flex-col items-center gap-0.5 disabled:opacity-50"
        >
          <div className="h-10 w-10 rounded-full bg-black/50 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:border-[#00FFFF]/60 hover:text-[#00FFFF] transition-all">
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <Download className="h-4 w-4" strokeWidth={1.8} />
            )}
          </div>
          <span className="text-white text-[10px] font-mono uppercase">
            {downloading ? "wait" : "save"}
          </span>
        </button>
      </div>

      {/* Hidden dubbed-audio track — mounted only when a translation is
          active. The sync effect above keeps its currentTime locked to the
          native <video> so the dub tracks what's happening on screen. */}
      {translateLang && translateUrl && (
        <audio
          ref={translateAudioRef}
          src={translateUrl}
          preload="auto"
          data-testid={`translation-audio-${video.id}`}
          className="hidden"
        />
      )}

      <CommentDrawer
        open={showComments}
        onClose={() => setShowComments(false)}
        video={video}
        onCommentAdded={onUpdate}
      />

      <TipDialog
        open={showTip}
        onClose={() => {
          setShowTip(false);
          setStealMode(null);
        }}
        video={video}
        initialCustomAmount={stealMode?.amount}
        initialMessage={stealMode?.message}
        onWalletTipSuccess={() => {
          setStealMode(null);
          onUpdate?.();
        }}
      />

      {/* Exit-intent hook — "wait, you didn't grab this?" mini card that
          shrinks into the top-right corner of the NEXT video for a few
          seconds after the viewer swipes away from a shop video. */}
      {exitIntent && (
        <div
          data-testid={`exit-intent-${video.id}`}
          className="absolute top-4 right-4 z-40 w-56 pointer-events-auto animate-[vvRefIn_0.4s_cubic-bezier(0.22,1,0.36,1)]"
        >
          <div className="relative bg-black/85 backdrop-blur-md border-2 border-[#FF9500] shadow-[0_0_25px_rgba(255,149,0,0.55)]">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExitIntent(null); }}
              className="absolute top-1 right-1 z-10 h-5 w-5 rounded-full bg-black/70 border border-white/20 flex items-center justify-center text-white/70 hover:text-white text-xs"
              data-testid={`exit-intent-close-${video.id}`}
            >✕</button>
            <button
              type="button"
              data-testid={`exit-intent-buy-${video.id}`}
              onClick={(e) => {
                e.stopPropagation();
                try {
                  const ping = `${API}/videos/${exitIntent.video_id}/product-click`;
                  if (navigator.sendBeacon) navigator.sendBeacon(ping);
                  else fetch(ping, { method: "POST", keepalive: true }).catch(() => {});
                } catch { /* ignore */ }
                window.open(exitIntent.product_url, "_blank", "noopener,noreferrer");
                setExitIntent(null);
              }}
              className="block w-full text-left group"
            >
              <div className="aspect-video bg-black/50 overflow-hidden">
                <img
                  src={
                    exitIntent.product_image_custom_path
                      ? `${API}/videos/${exitIntent.video_id}/product-image`
                      : exitIntent.product_image_url
                  }
                  alt={exitIntent.product_title || "product"}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-2 space-y-1">
                <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#FF9500]">
                  // wait
                </p>
                <p className="text-xs font-bold text-white line-clamp-2 leading-tight">
                  Didn&apos;t grab this?
                </p>
                <p className="text-[10px] text-white/60 truncate">
                  {exitIntent.product_title || "Buy now"}
                </p>
                <p className="text-[10px] font-mono text-[#FF9500] font-bold flex items-center gap-1 pt-1 group-hover:underline">
                  <ShoppingBag className="h-3 w-3" strokeWidth={2.5} />
                  buy now →
                </p>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Product action picker — shows when the SHOP thumbnail is tapped.
          Two options: BUY (opens shop) or SHARE (opens social share sheet). */}
      {productMenuOpen && (
        <div
          data-testid={`product-menu-${video.id}`}
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setProductMenuOpen(false)}
        >
          <div
            className="w-full max-w-sm bg-[#0A0A0C] border border-[#FF9500]/50 shadow-[0_0_35px_rgba(255,149,0,0.35)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Big for-sale hero. Single image → static tappable frame.
                Multiple images → horizontal snap-scroll gallery with dot
                indicator (like Instagram shopping). Tap any image opens
                the full-screen zoom modal at that index. */}
            <div className="relative w-full bg-black" style={{ height: "50vh" }}>
              {productImages.length > 1 ? (
                <div
                  className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-none"
                  onScroll={(e) => {
                    const idx = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth);
                    if (idx !== productImageIdx) setProductImageIdx(idx);
                  }}
                >
                  {productImages.map((src, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`product-image-${i}-${video.id}`}
                      onClick={() => { setProductImageIdx(i); setImageZoomOpen(true); }}
                      className="shrink-0 w-full h-full snap-center"
                    >
                      <img src={src} alt={video.product_title || "product"} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={`product-image-0-${video.id}`}
                  onClick={() => setImageZoomOpen(true)}
                  className="w-full h-full block"
                >
                  <img
                    src={productImages[0]}
                    alt={video.product_title || "product"}
                    className="w-full h-full object-cover"
                  />
                </button>
              )}
              {/* Dot indicator — only rendered when there's more than one
                  image, otherwise the single dot is visual noise. */}
              {productImages.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
                  {productImages.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${
                        i === productImageIdx ? "w-5 bg-[#FF9500]" : "w-1.5 bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              )}
              {/* Tiny "TAP TO ZOOM" hint in the corner — fades after 3s so
                  it doesn't clutter, gives first-timers the affordance. */}
              <span
                className="absolute top-2 right-2 font-mono text-[9px] uppercase tracking-widest px-2 py-1 bg-black/60 text-white/70 border border-white/20 pointer-events-none"
                style={{ animation: "hintFade 4s ease-out forwards" }}
              >
                tap to zoom
              </span>
              <style>{`
                @keyframes hintFade {
                  0%, 40% { opacity: 1; }
                  100%    { opacity: 0; }
                }
              `}</style>
              <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
            </div>
            <div className="px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500]">
                // shop.or.share
              </p>
              <p className="text-base font-bold text-white truncate mt-1">
                {video.product_title || "This item"}
              </p>
              <p className="text-[11px] font-mono text-white/40 truncate">
                guyanadiscounts.com
              </p>
            </div>
            {/* Delivery banner — sits between the product info block and
                the buy/share grid. Animated moving shimmer + neon pink
                glow so it catches every eye the moment the picker opens.
                Only shown for guyanadiscounts.com since that's the only
                domain we actually deliver from right now. Tapping opens
                the delivery-info panel with a map + list of coverage. */}
            {(video.product_url || "").includes("guyanadiscounts.com") && (
              <button
                type="button"
                data-testid={`product-delivery-banner-${video.id}`}
                onClick={(e) => { e.stopPropagation(); setDeliveryInfoOpen(true); }}
                className="w-full relative overflow-hidden border-y border-[#FF007F]/60 bg-[#FF007F]/10 cursor-pointer hover:bg-[#FF007F]/15 transition-colors"
              >
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FF007F]/40 to-transparent"
                  style={{ animation: "delivery-shimmer 2.2s linear infinite" }}
                />
                <p
                  className="relative py-2.5 text-center font-black text-base tracking-[0.15em] uppercase text-white"
                  style={{
                    textShadow: "0 0 12px rgba(255,0,127,0.9), 0 0 22px rgba(255,0,127,0.5)",
                    animation: "delivery-pulse 1.6s ease-in-out infinite",
                  }}
                >
                  🚚 Delivery around Guyana
                </p>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[9px] uppercase tracking-widest text-white/60">
                  tap ›
                </span>
                <style>{`
                  @keyframes delivery-shimmer {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                  }
                  @keyframes delivery-pulse {
                    0%, 100% { opacity: 1;   transform: scale(1); }
                    50%      { opacity: 0.85; transform: scale(1.03); }
                  }
                `}</style>
              </button>
            )}
            <div className="grid grid-cols-2 divide-x divide-white/10 border-t border-white/10">
              <button
                type="button"
                data-testid={`product-buy-btn-${video.id}`}
                onClick={buyNow}
                className="py-3 flex flex-col items-center gap-1 hover:bg-[#FF9500]/10 group"
              >
                <ShoppingBag className="h-5 w-5 text-[#FF9500] group-hover:scale-110 transition-transform" strokeWidth={2} />
                <span className="text-[11px] font-mono uppercase tracking-widest text-white group-hover:text-[#FF9500]">buy item</span>
              </button>
              <button
                type="button"
                data-testid={`product-share-btn-${video.id}`}
                onClick={() => { setProductMenuOpen(false); setShareOpen(true); }}
                className="py-3 flex flex-col items-center gap-1 hover:bg-[#39FF14]/10 group"
              >
                <Share2 className="h-5 w-5 text-[#39FF14] group-hover:scale-110 transition-transform" strokeWidth={2} />
                <span className="text-[11px] font-mono uppercase tracking-widest text-white group-hover:text-[#39FF14]">share video</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery-Info Panel — opens when the "Delivery around Guyana"
          banner is tapped. ~60vh tall stylized map card of Guyana with
          animated location pins + checklist of covered towns. X in the
          top-right so users know it can be dismissed. Stacked ABOVE the
          product picker (z-70 vs 60) so the picker stays visible behind
          the darkened backdrop. */}
      {deliveryInfoOpen && (
        <div
          data-testid={`delivery-info-panel-${video.id}`}
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center px-4"
          onClick={() => setDeliveryInfoOpen(false)}
        >
          <div
            className="w-full max-w-md bg-gradient-to-br from-[#0A0A0C] to-[#1a0a14] border-2 border-[#FF007F] shadow-[0_0_40px_rgba(255,0,127,0.55)] overflow-hidden relative"
            style={{ maxHeight: "70vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* X close — top-right, always tappable no matter the scroll */}
            <button
              type="button"
              onClick={() => setDeliveryInfoOpen(false)}
              data-testid={`delivery-info-close-${video.id}`}
              className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-[#FF007F] hover:border-[#FF007F] transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>

            {/* Header */}
            <div className="px-5 pt-5 pb-3 text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FF007F]">
                // ios.matrixshopping.com
              </p>
              <h3
                className="mt-1 font-black text-2xl tracking-wide text-white"
                style={{ textShadow: "0 0 14px rgba(255,0,127,0.85)" }}
              >
                DELIVERY <span className="text-[#39FF14]">AROUND</span> GUYANA
              </h3>
              <p className="text-white/50 text-xs font-mono uppercase tracking-widest mt-1">
                shop online · we deliver
              </p>
            </div>

            {/* WE DELIVER TO checklist */}
            <div className="px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FFCC00] mb-2">
                // we deliver to
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {["Georgetown", "Linden", "Berbice", "New Amsterdam",
                  "Skeldon", "Timehri", "Parika", "Lethem", "Bartica"].map((loc) => (
                  <div key={loc} className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-[#39FF14] shrink-0" strokeWidth={3} />
                    <span className="text-white text-sm">{loc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer CTA */}
            <div className="border-t border-white/10 px-5 py-3 bg-[#FF007F]/10 text-center">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/60">
                fast · reliable · safe
              </p>
              <p className="text-white text-xs mt-1">
                We bring your shopping to your door
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen product image zoom. Tap anywhere to close. If more
          than one angle exists, horizontal snap-scroll between them with
          the current index kept in sync with the picker's dot indicator. */}
      {imageZoomOpen && productImages.length > 0 && (
        <div
          data-testid={`product-image-zoom-${video.id}`}
          className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center"
          onClick={() => setImageZoomOpen(false)}
        >
          <button
            type="button"
            data-testid={`product-image-zoom-close-${video.id}`}
            onClick={(e) => { e.stopPropagation(); setImageZoomOpen(false); }}
            className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20"
            aria-label="Close zoom"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
          {productImages.length > 1 ? (
            <div
              className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-none"
              onScroll={(e) => {
                const idx = Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth);
                if (idx !== productImageIdx) setProductImageIdx(idx);
              }}
              ref={(el) => {
                // Jump to the currently-selected index when zoom opens so
                // the modal isn't stuck on image 0 regardless of which
                // thumbnail the user tapped.
                if (el && el.scrollLeft === 0 && productImageIdx > 0) {
                  el.scrollLeft = productImageIdx * el.clientWidth;
                }
              }}
            >
              {productImages.map((src, i) => (
                <div key={i} className="shrink-0 w-full h-full snap-center flex items-center justify-center px-4">
                  <img
                    src={src}
                    alt={`${video.product_title || "product"} — angle ${i + 1}`}
                    className="max-h-full max-w-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ))}
            </div>
          ) : (
            <img
              src={productImages[0]}
              alt={video.product_title || "product"}
              className="max-h-[90vh] max-w-[92vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {productImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 pointer-events-none">
              {productImages.map((_, i) => (
                <span
                  key={i}
                  className={`h-2 rounded-full transition-all ${
                    i === productImageIdx ? "w-6 bg-[#FF9500]" : "w-2 bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* End-of-video BUY NOW placecard — big call-to-action when the
          video finishes for shop-attached videos. */}
      {endCardOpen && video.product_url && (
        <div
          data-testid={`end-card-${video.id}`}
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-30 mx-4 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative bg-black/85 backdrop-blur-md border-2 border-[#FF9500] shadow-[0_0_35px_rgba(255,149,0,0.55)] overflow-hidden max-w-md mx-auto">
            <button
              type="button"
              data-testid={`end-card-close-${video.id}`}
              onClick={(e) => { e.stopPropagation(); setEndCardOpen(false); }}
              className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-black/80"
            >
              ✕
            </button>
            <div className="aspect-video bg-black/50">
              <img
                src={
                  video.product_image_custom_path
                    ? `${API}/videos/${video.id}/product-image`
                    : video.product_image_url
                }
                alt={video.product_title || "product"}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4 text-center space-y-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500]">
                // featured.product
              </p>
              <p className="font-display font-black text-lg text-white line-clamp-2">
                {video.product_title || "Grab this item"}
              </p>
              <button
                type="button"
                data-testid={`end-card-buy-${video.id}`}
                onClick={(e) => { e.stopPropagation(); buyNow(); }}
                className="w-full bg-[#FF9500] text-black font-display font-black py-3 text-lg uppercase tracking-wider hover:bg-[#FFB84D] transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingBag className="h-5 w-5" strokeWidth={2.5} />
                buy now
                <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
              </button>
              <p className="text-[10px] font-mono text-white/40">
                opens guyanadiscounts.com in a new tab
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Share sheet — social network launcher */}
      {shareOpen && (        <div
          data-testid={`share-sheet-${video.id}`}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-full max-w-md bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_35px_rgba(57,255,20,0.3)] p-5 space-y-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
                  // share.video
                </p>
                <h3 className="font-display font-black text-2xl mt-1">Send this everywhere</h3>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                data-testid={`share-close-${video.id}`}
                className="p-1 text-white/50 hover:text-white"
              >
                <span className="sr-only">Close</span>
                ✕
              </button>
            </div>

            {/* Copyable URL row */}
            <div className="flex items-center gap-2 border border-white/10 bg-black/40 px-3 py-2">
              <span className="flex-1 font-mono text-[11px] text-white/60 truncate">
                {shareUrl}
              </span>
              <button
                type="button"
                data-testid={`share-copy-${video.id}`}
                onClick={copyLink}
                className="shrink-0 flex items-center gap-1 border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider"
              >
                <Copy className="h-3 w-3" strokeWidth={2} />
                copy
              </button>
            </div>

            {/* Social grid */}
            <div className="grid grid-cols-4 gap-3">
              <ShareTile
                testid={`share-whatsapp-${video.id}`}
                onClick={() => shareVia("whatsapp")}
                icon={
                  // Custom WhatsApp glyph — Lucide doesn't ship one; inline SVG.
                  <svg viewBox="0 0 32 32" className="h-6 w-6 fill-current">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.393-1.46-2.014a.42.42 0 0 1-.093-.235c0-.372.402-.512.687-.66.088-.056.09-.096.126-.146.113-.15.213-.31.312-.463.077-.116.115-.24.128-.372a.31.31 0 0 0-.014-.14c-.093-.286-.815-1.918-1.005-2.335a.86.86 0 0 0-.253-.253c-.13-.037-.267-.05-.402-.037-.203.014-.407.05-.6.14-.443.204-.798.55-1.058.982-.348.577-.535 1.234-.542 1.905 0 .187.028.377.056.564.14.855.484 1.685 1.014 2.4C13.05 18.4 15.8 20.7 18.9 21.4c.303.07.61.113.92.13.19.014.376.014.564 0 .577-.055 1.137-.222 1.633-.485.315-.164.577-.4.75-.7a1.7 1.7 0 0 0 .12-1.005c-.14-.42-.66-.577-1.033-.796-.372-.222-.777-.372-1.174-.55a.87.87 0 0 0-.567-.14z"/>
                    <path d="M28 6.4A16 16 0 0 0 3.4 25.3l-1.4 5.1 5.2-1.36A15.9 15.9 0 0 0 16 32C24.837 32 32 24.837 32 16A15.9 15.9 0 0 0 28 6.4zM16 29.42a13.4 13.4 0 0 1-6.83-1.86l-.49-.29-3.084.8.82-3-.32-.5A13.4 13.4 0 1 1 16 29.42z"/>
                  </svg>
                }
                label="WhatsApp"
                color="#25D366"
              />
              <ShareTile
                testid={`share-facebook-${video.id}`}
                onClick={() => shareVia("facebook")}
                icon={<Facebook className="h-6 w-6" fill="currentColor" strokeWidth={0} />}
                label="Facebook"
                color="#1877F2"
              />
              <ShareTile
                testid={`share-messenger-${video.id}`}
                onClick={() => shareVia("messenger")}
                icon={<MessageCircle className="h-6 w-6" fill="currentColor" strokeWidth={0} />}
                label="Messenger"
                color="#00B2FF"
              />
              <ShareTile
                testid={`share-twitter-${video.id}`}
                onClick={() => shareVia("twitter")}
                icon={<Twitter className="h-6 w-6" fill="currentColor" strokeWidth={0} />}
                label="X / Twitter"
                color="#FFFFFF"
              />
              <ShareTile
                testid={`share-telegram-${video.id}`}
                onClick={() => shareVia("telegram")}
                icon={<Send className="h-6 w-6" fill="currentColor" strokeWidth={0} />}
                label="Telegram"
                color="#26A5E4"
              />
              <ShareTile
                testid={`share-sms-${video.id}`}
                onClick={() => shareVia("sms")}
                icon={<MessageSquare className="h-6 w-6" strokeWidth={1.8} />}
                label="SMS"
                color="#39FF14"
              />
              <ShareTile
                testid={`share-email-${video.id}`}
                onClick={() => shareVia("email")}
                icon={<Mail className="h-6 w-6" strokeWidth={1.8} />}
                label="Email"
                color="#FF9500"
              />
              {typeof navigator !== "undefined" && typeof navigator.share === "function" && (
                <ShareTile
                  testid={`share-native-${video.id}`}
                  onClick={shareNative}
                  icon={<Share2 className="h-6 w-6" strokeWidth={1.8} />}
                  label="More…"
                  color="#B026FF"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Creator-only sharers modal — thank + reward top sharers */}
      {sharersOpen && (
        <div
          data-testid={`sharers-modal-${video.id}`}
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setSharersOpen(false)}
        >
          <div
            className="w-full max-w-md bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_35px_rgba(57,255,20,0.3)] p-5 space-y-3 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
                  // your.top.sharers
                </p>
                <h3 className="font-display font-black text-xl mt-1">Reward who&apos;s spreading you</h3>
                <p className="text-[11px] text-white/50 mt-1">
                  Send coins to fans who share your video. 100% goes to them — no platform cut.
                </p>
              </div>
              <button
                type="button"
                data-testid="sharers-modal-close"
                onClick={() => setSharersOpen(false)}
                className="p-1 text-white/50 hover:text-white"
              >✕</button>
            </div>

            {sharersLoading ? (
              <div className="py-10 flex justify-center">
                <div className="h-6 w-6 border-2 border-[#39FF14] border-t-transparent animate-spin rounded-full" />
              </div>
            ) : sharersList.length === 0 ? (
              <p className="text-white/40 italic py-8 text-center text-sm">
                Nobody has shared this video yet.
              </p>
            ) : (
              <div className="divide-y divide-white/5">
                {sharersList.map((s, i) => (
                  <div key={s.user_id || i} className="py-2.5" data-testid={`sharer-item-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 text-center font-mono text-white/40 text-xs">#{i + 1}</div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-white truncate">
                          @{s.username || "anonymous"}
                        </p>
                        <p className="text-[10px] font-mono text-white/40">
                          {s.visits} visits · {s.unique_viewers} unique
                          {s.already_rewarded_coins > 0 && (
                            <span className="ml-2 text-[#FFD700]">
                              · rewarded {s.already_rewarded_coins} coins
                            </span>
                          )}
                        </p>
                      </div>
                      {s.user_id && (
                        <button
                          type="button"
                          data-testid={`sharer-reward-btn-${i}`}
                          onClick={() => setRewardTarget(rewardTarget === s.user_id ? null : s.user_id)}
                          className="shrink-0 border border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10 text-[10px] font-mono uppercase tracking-wider px-2 py-1"
                        >
                          {rewardTarget === s.user_id ? "cancel" : "reward"}
                        </button>
                      )}
                    </div>
                    {rewardTarget === s.user_id && s.user_id && (
                      <div className="mt-2 pl-11 space-y-2">
                        <input
                          type="number"
                          min={10}
                          max={100000}
                          step={10}
                          value={rewardAmount}
                          data-testid={`reward-amount-${i}`}
                          onChange={(e) => setRewardAmount(e.target.value)}
                          className="w-full bg-black/40 border border-white/15 px-2 py-1.5 text-sm text-white focus:border-[#FFD700] focus:outline-none font-mono"
                        />
                        <div className="flex gap-1">
                          {[50, 100, 250, 500].map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setRewardAmount(String(c))}
                              className="flex-1 border border-white/10 hover:border-[#FFD700] py-1 text-[10px] font-mono text-white/60"
                            >{c}c</button>
                          ))}
                        </div>
                        <p className="text-[10px] font-mono text-white/40">
                          = ${((parseInt(rewardAmount || "0", 10) || 0) / 100).toFixed(2)}
                        </p>
                        <button
                          type="button"
                          data-testid={`reward-send-${i}`}
                          onClick={() => sendReward(s.user_id)}
                          disabled={rewardBusy}
                          className="w-full bg-[#FFD700] text-black py-1.5 text-xs font-mono uppercase tracking-wider disabled:opacity-40"
                        >
                          {rewardBusy ? "sending…" : `send ${rewardAmount} coins to @${s.username}`}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Small inline sub-component — icon + label tile used in the share sheet.
function ShareTile({ onClick, icon, label, color, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex flex-col items-center gap-1.5 group"
    >
      <div
        className="h-14 w-14 rounded-full flex items-center justify-center border-2 transition-all group-hover:scale-110"
        style={{
          borderColor: `${color}55`,
          color,
          background: `${color}10`,
        }}
      >
        {icon}
      </div>
      <span className="text-white/70 text-[10px] font-mono uppercase tracking-wider">
        {label}
      </span>
    </button>
  );
}

// Author avatar that interchanges with the creator's top-3 badge every
// 5 seconds when the author is currently ranked. `useTopBadges` reads
// from the shared 7-day cache. When there's no badge we render the
// plain avatar. Tapping while the badge face is showing pops the full
// -screen celebration animation (stopPropagation so it doesn't also
// navigate to the profile via the parent <Link>).
function AuthorAvatarWithBadge({ authorId, username, picture, name }) {
  const badge = useTopBadges(authorId);
  const [showingBadge, setShowingBadge] = React.useState(false);
  const [celebrating, setCelebrating] = React.useState(false);

  React.useEffect(() => {
    if (!badge) { setShowingBadge(false); return; }
    // Kick the swap immediately so viewers see the badge face on the
    // first paint, then flip every 5s.
    setShowingBadge(true);
    const t = setInterval(() => setShowingBadge((s) => !s), 5000);
    return () => clearInterval(t);
  }, [badge?.category, badge?.rank]);

  const avatar = picture ? (
    <img
      src={picUrl(picture)}
      alt=""
      className="h-10 w-10 object-cover border-2 border-[#39FF14]/60"
    />
  ) : (
    <div className="h-10 w-10 bg-[#FF007F]/20 flex items-center justify-center font-display font-bold text-[#FF007F] border-2 border-[#FF007F]/40">
      {name?.[0]?.toUpperCase() || "?"}
    </div>
  );

  if (!badge) return avatar;

  // Two absolutely-positioned faces cross-faded via opacity — keeps the
  // 40x40 footprint identical to the plain-avatar path so surrounding
  // layout doesn't shift when a user gains or loses their badge.
  return (
    <>
      <div className="relative h-10 w-10">
        <div
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: showingBadge ? 0 : 1 }}
        >
          {avatar}
        </div>
        <button
          type="button"
          onClick={(e) => {
            if (!showingBadge) return;
            e.preventDefault();
            e.stopPropagation();
            setCelebrating(true);
          }}
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: showingBadge ? 1 : 0, pointerEvents: showingBadge ? "auto" : "none" }}
          aria-label={`${badge.category === "sharer" ? "Top Sharer" : "Top Referrer"} rank ${badge.rank}`}
          data-testid={`author-avatar-badge-${username || "creator"}`}
        >
          <CreatorBadge
            category={badge.category}
            rank={badge.rank}
            size="md"
            mode="static"
          />
        </button>
      </div>
      {celebrating && (
        <BadgeCelebration
          category={badge.category}
          rank={badge.rank}
          username={username}
          onClose={() => setCelebrating(false)}
        />
      )}
    </>
  );
}

